import { useRef, useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  FIELD_CLS, PRIMARY_BTN_SM, SECONDARY_BTN, GHOST_ACCENT_BTN, ICON_TILES, SectionCard, Badge, Chip, EmptyState,
  InfoTooltip, aperturaRinnovo, rinnovoAperto, statoScadenza, descrizionePeriodo,
  NumberField, SegmentedControl, InfoHint, ToggleSwitch, DatePicker,
} from "../components/designSystem";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "../components/ui/alert-dialog";
import { buttonVariants } from "../components/ui/button";
import {
  FileSignature, Plus, ChevronLeft, ChevronRight, Building2, UserCircle, Check,
  Loader2, FileText, CalendarClock, Shuffle, AlertCircle, Upload, ChevronDown,
  Package, Eye, Send, Copy, Camera, Search, RefreshCw, MoreHorizontal, Mail, X, SlidersHorizontal, CalendarDays,
} from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "../utils/api";
import { formatCurrency } from "../utils/formatters";
import { AMBITI_CONTRATTO, ambitoDi } from "../utils/ambitiContratto";

// ==================== Design tokens (design handoff "Anagrafica contratto", variante 1c) ====================
// Font scoped al modulo via .contratto-font-ui / .contratto-font-mono (definite in index.css),
// non il font di prodotto: è un modulo con la sua identità visiva, riprodotta fedelmente dal
// prototipo consegnato. I colori restano hex letterali (non i token indigo del resto dell'app)
// per la stessa ragione — vedi README del design handoff.
const INK900 = "#15161c";
const INK600 = "#565d6d";
const INK400 = "#9095a1";
const IND = "#5b5bd6";
const SUCCESS = "#2f7a53";
const DANGER = "#b4474b";

const INPUT_BASE = "h-[42px] px-3 rounded-[10px] border text-[14.5px] font-medium outline-none transition-colors focus:border-[#5b5bd6] focus:shadow-[0_0_0_3px_rgba(91,91,214,.14)]";
const INPUT_NEUTRAL = "border-[#e4e4ed] bg-white text-[#15161c]";

// CAP → comune/provincia: mappa statica di 7 CAP (stessa del prototipo). In produzione va
// collegata al servizio/anagrafica CAP reale, con gestione di CAP non trovato e CAP condivisi
// da più comuni (in quel caso proporre una lista) — vedi README.
const CAPS = {
  "20121": { comune: "Milano", prov: "MI" },
  "23964": { comune: "Valgreghentino", prov: "LC" },
  "00184": { comune: "Roma", prov: "RM" },
  "80121": { comune: "Napoli", prov: "NA" },
  "50123": { comune: "Firenze", prov: "FI" },
  "10121": { comune: "Torino", prov: "TO" },
  "40121": { comune: "Bologna", prov: "BO" },
};
const MESI_ITALIANI = ["gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno", "luglio", "agosto", "settembre", "ottobre", "novembre", "dicembre"];

const ANAGRAFICA_VUOTA = {
  ragione_sociale: "",
  cf_piva: "",
  sede_via: "",
  sede_comune: "",
  sede_prov: "",
  sede_cap: "",
  sede_cap_auto: false,
  sede_stato: "Italia",
  codice_univoco: "",
  referente: "",
  referente_cf: "",
  recapito_telefonico: "",
  email: "",
  pec: "",
};

const LEGALE_VUOTO = {
  nome: "",
  cognome: "",
  cf: "",
  residente_via: "",
  residente_comune: "",
  residente_prov: "",
  residente_cap: "",
  residente_cap_auto: false,
  residente_stato: "Italia",
  documento_tipo: "",
  documento_numero: "",
  documento_rilasciato_da: "",
  documento_data_rilascio: "",
  documento_scadenza: "",
};

// Icone del dropdown "Tipo documento" — stessi path del prototipo (tessera
// orizzontale per la patente, tessera con ritratto per la CI, libretto per il passaporto).
function IconaPatente(props) {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" {...props}>
      <rect x="2" y="5" width="20" height="14" rx="2.5" />
      <circle cx="8" cy="12" r="2.2" />
      <path d="M13 10h6M13 14h4" />
    </svg>
  );
}
function IconaCartaIdentita(props) {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" {...props}>
      <rect x="3" y="4" width="18" height="16" rx="2.5" />
      <circle cx="9" cy="10" r="2.2" />
      <path d="M14 9h4M14 13h4M5.5 17c1-1.6 2-2.2 3.5-2.2s2.5.6 3.5 2.2" />
    </svg>
  );
}
function IconaPassaporto(props) {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" {...props}>
      <path d="M5 3h12a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5z" />
      <circle cx="12" cy="10" r="3" />
      <path d="M9 17h6" />
    </svg>
  );
}

const DOCUMENTI = [
  { valore: "Patente", titolo: "Patente", descrizione: "Patente di guida, fronte e retro", Icona: IconaPatente },
  { valore: "Carta d'identità", titolo: "Carta d'identità", descrizione: "Cartacea o elettronica (CIE)", Icona: IconaCartaIdentita },
  { valore: "Passaporto", titolo: "Passaporto", descrizione: "Pagina con foto e dati", Icona: IconaPassaporto },
];

const FORMATI_DOCUMENTO_CONSENTITI = ["image/jpeg", "image/png", "image/webp", "application/pdf"];
const DOCUMENTO_MAX_SIZE_BYTES = 10 * 1024 * 1024; // 10MB, in sync con backend/routes/contratti.py

const DURATA_PRESET = [
  { mesi: 1, label: "1 mese" },
  { mesi: 3, label: "3 mesi" },
  { mesi: 6, label: "6 mesi" },
  { mesi: 12, label: "1 anno" },
];

// CRA imposta NODE_ENV="development" solo con `yarn start`: in build di
// produzione (`yarn build`, quella servita da Railway) vale "production", quindi
// il pulsante di compilazione rapida sparisce da solo senza bisogno di un flag ad hoc.
const IS_LOCAL_DEV = process.env.NODE_ENV === "development";

const AZIENDE_PROVA = ["Rossi Costruzioni", "Bianchi Marketing", "Verdi Consulting", "Gialli Design", "Neri Logistics", "Colombo Trading"];
const VIE_PROVA = ["Roma", "Milano", "Dante Alighieri", "Garibaldi", "Mazzini", "Verdi"];
const NOMI_PROVA = ["Mario", "Luca", "Anna", "Giulia", "Marco", "Sara", "Paolo", "Elena"];
const COGNOMI_PROVA = ["Rossi", "Bianchi", "Verdi", "Ferrari", "Colombo", "Ricci", "Marino", "Greco"];

function cifreCasuali(n) {
  return Array.from({ length: n }, () => Math.floor(Math.random() * 10)).join("");
}

function elementoCasuale(lista) {
  return lista[Math.floor(Math.random() * lista.length)];
}

// Genera una P.IVA che supera il checksum di pivaCheck (Luhn a 11 cifre): utile
// solo per il pulsante "Dati random" così mostra subito lo stato "valida".
function generaPivaValida() {
  const cifre = Array.from({ length: 10 }, () => Math.floor(Math.random() * 10));
  let somma = 0;
  for (let i = 0; i < 10; i++) {
    let n = cifre[i];
    if (i % 2 === 1) { n *= 2; if (n > 9) n -= 9; }
    somma += n;
  }
  const controllo = (10 - (somma % 10)) % 10;
  return [...cifre, controllo].join("");
}

// Genera un C.F. che rispetta il formato di cfCheck (non il carattere di controllo reale,
// solo la forma AAAAAA00A00A000A) — stesso scopo del generatore P.IVA.
function generaCfValido() {
  const lettera = () => String.fromCharCode(65 + Math.floor(Math.random() * 26));
  const cifra = () => String(Math.floor(Math.random() * 10));
  return (
    lettera() + lettera() + lettera() + lettera() + lettera() + lettera()
    + cifra() + cifra() + lettera()
    + cifra() + cifra() + lettera()
    + cifra() + cifra() + cifra() + lettera()
  );
}

function generaDatiProvaCasuali() {
  const capChiavi = Object.keys(CAPS);
  const capSede = elementoCasuale(capChiavi);
  const hitSede = CAPS[capSede];
  const capResidenza = elementoCasuale(capChiavi);
  const hitResidenza = CAPS[capResidenza];
  return {
    anagrafica: {
      ragione_sociale: `${elementoCasuale(AZIENDE_PROVA)} S.r.l.`,
      cf_piva: generaPivaValida(),
      sede_via: `Via ${elementoCasuale(VIE_PROVA)} ${Math.floor(Math.random() * 100) + 1}`,
      sede_cap: capSede,
      sede_comune: hitSede.comune,
      sede_prov: hitSede.prov,
      sede_cap_auto: true,
      sede_stato: "Italia",
      codice_univoco: cifreCasuali(7),
      referente: `${elementoCasuale(NOMI_PROVA)} ${elementoCasuale(COGNOMI_PROVA)}`,
      referente_cf: generaCfValido(),
      recapito_telefonico: `3${cifreCasuali(9)}`,
      email: `prova${cifreCasuali(4)}@esempio.it`,
      pec: `prova${cifreCasuali(4)}@pec.it`,
    },
    legale: {
      nome: elementoCasuale(NOMI_PROVA),
      cognome: elementoCasuale(COGNOMI_PROVA),
      cf: generaCfValido(),
      residente_via: `Via ${elementoCasuale(VIE_PROVA)} ${Math.floor(Math.random() * 100) + 1}`,
      residente_cap: capResidenza,
      residente_comune: hitResidenza.comune,
      residente_prov: hitResidenza.prov,
      residente_cap_auto: true,
      documento_tipo: elementoCasuale(DOCUMENTI).valore,
      documento_numero: cifreCasuali(9),
      documento_rilasciato_da: `Comune di ${hitResidenza.comune}`,
      documento_data_rilascio: "2022-03-15",
      documento_scadenza: "2032-03-15",
      residente_stato: "Italia",
    },
  };
}

// ==================== Validazione live P.IVA / Codice Fiscale ====================
// Algoritmi come da README del design handoff: checksum P.IVA (Luhn 11 cifre) e
// formato C.F. (il carattere di controllo reale non è coperto, così come nel prototipo).
function pivaCheck(valore) {
  const d = (valore || "").replace(/\D/g, "");
  if (!d) return { colore: INK400, messaggio: "11 cifre per P.IVA, 16 caratteri per C.F.", valida: false, esito: null };
  if (d.length < 11) {
    const n = 11 - d.length;
    return { colore: INK400, messaggio: n === 1 ? "1 cifra mancante" : `${n} cifre mancanti`, valida: false, esito: null };
  }
  if (d.length > 11) return { colore: DANGER, messaggio: "Troppe cifre: sono 11", valida: false, esito: "bad" };
  let somma = 0;
  for (let i = 0; i < 11; i++) {
    let n = +d[i];
    if (i % 2 === 1) { n *= 2; if (n > 9) n -= 9; }
    somma += n;
  }
  return somma % 10 === 0
    ? { colore: SUCCESS, messaggio: "P.IVA valida", valida: true, esito: "ok" }
    : { colore: DANGER, messaggio: "Cifra di controllo errata", valida: false, esito: "bad" };
}

function cfCheck(valore) {
  const s = (valore || "").toUpperCase();
  if (!s) return { colore: INK400, messaggio: "16 caratteri", valida: false, esito: null };
  if (s.length < 16) {
    const n = 16 - s.length;
    return { colore: INK400, messaggio: n === 1 ? "1 carattere mancante" : `${n} caratteri mancanti`, valida: false, esito: null };
  }
  return /^[A-Z]{6}\d{2}[A-Z]\d{2}[A-Z]\d{3}[A-Z]$/.test(s)
    ? { colore: SUCCESS, messaggio: "Formato corretto", valida: true, esito: "ok" }
    : { colore: DANGER, messaggio: "Formato non valido", valida: false, esito: "bad" };
}

function formattaDataItaliana(data) {
  return `${data.getDate()} ${MESI_ITALIANI[data.getMonth()]} ${data.getFullYear()}`;
}

// Data locale -> "YYYY-MM-DD", il formato di DatePicker e del backend. Non
// passa da toISOString: convertendo in UTC sposterebbe il giorno a mezzanotte.
function dataIso(data) {
  return `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, "0")}-${String(data.getDate()).padStart(2, "0")}`;
}

function formattaDimensione(bytes) {
  // Le bozze riaperte prima di questa modifica non hanno size salvata lato backend.
  if (!Number.isFinite(bytes)) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function formattaEuro(valore) {
  return formatCurrency(valore, { decimals: 2 });
}

function formattaAddon(addon) {
  const valore = addon.tipo_valore === "percentuale" ? `${Number(addon.valore) || 0}%` : formattaEuro(addon.valore);
  const ricorrenza = addon.ricorrenza === "mensile" ? "al mese" : "una tantum";
  return `${addon.label} · ${valore} ${ricorrenza}`;
}

// ==================== Personalizzazione del servizio ====================
// Un servizio del catalogo si può adattare al cliente (PUT /contratti/{id}/servizi):
//   - voci del listino tolte (es. la quota variabile), anche l'attivazione
//     (VOCE_ATTIVAZIONE fra gli addonRimossi): il contratto non le nomina;
//   - sovrapprezzi: con etichetta sono una voce a sé nel contratto, senza si
//     sommano all'attivazione o alla quota mensile;
//   - sconti in € o in %, sull'attivazione o su una quota a importo fisso,
//     che il contratto mostra col listino barrato.
// Gli importi salvati li calcola il backend (personalizza_servizio in
// backend/proposta_economica.py); calcolaServizio ripete la stessa regola per
// l'anteprima: se cambia lì va cambiata anche qui.

const VOCE_ATTIVAZIONE = "attivazione";
const SUFFISSO_PERSONALIZZATO = " (personalizzato)";

let contatoreRighe = 0;
function nuovaChiaveRiga(prefisso) {
  contatoreRighe += 1;
  return `${prefisso}_${contatoreRighe}`;
}

// Stato del form dalla personalizzazione salvata sullo snapshot del servizio.
function statoPersonalizzazione(servizio) {
  return {
    righeSovrapprezzi: (servizio.sovrapprezzi || []).map((x) => ({
      chiave: nuovaChiaveRiga("sovr"),
      sovrapprezzo_id: x.sovrapprezzo_id,
      label: x.label || "",
      ricorrenza: x.ricorrenza,
      valore: String(x.valore ?? ""),
    })),
    addonRimossi: [...(servizio.addon_rimossi || [])],
    righeSconti: (servizio.sconti || []).map((x) => ({
      chiave: nuovaChiaveRiga("sconto"),
      sconto_id: x.sconto_id,
      voce: x.voce,
      tipo_valore: x.tipo_valore,
      valore: String(x.valore ?? ""),
    })),
  };
}

function personalizzato(s) {
  return (s.righeSovrapprezzi || []).length > 0 || (s.addonRimossi || []).length > 0 || (s.righeSconti || []).length > 0;
}

const arrotonda = (n) => Math.round(n * 100) / 100;
const importoFisso = (a) => (a.tipo_valore || "importo") === "importo";

// Importi del servizio `base` (listino) con la personalizzazione dello stato
// del form `s`. Senza `s` sono quelli di listino. Nel `rinnovo` l'attivazione
// non è dovuta: il backend la toglie dallo snapshot come una voce tolta.
// Nome dell'attivazione principale: i servizi eventi possono darle un nome
// (backend/routes/catalogo_servizi.py), gli altri restano «Attivazione».
function nomeAttivazione(base) {
  return base?.nome_attivazione || "Attivazione";
}

function calcolaServizio(base, s = {}, rinnovo = false) {
  const rimossi = s.addonRimossi || [];
  const sconti = s.righeSconti || [];
  const conAttivazione = !rinnovo && !rimossi.includes(VOCE_ATTIVAZIONE);
  let attivazione = conAttivazione ? Number(base.importo_attivazione) || 0 : 0;
  const errori = [];
  // Nel rinnovo cadono anche le attivazioni successive alla prima, come nel
  // backend (proposta_economica.personalizza_servizio).
  const addons = (base.addons || [])
    .filter((a) => !rimossi.includes(a.addon_id) && !(rinnovo && a.attivazione))
    .map((a) => ({ ...a, valore: Number(a.valore) || 0, diListino: true }));
  (s.righeSovrapprezzi || []).forEach((x) => {
    const valore = Number(x.valore) || 0;
    if (x.label.trim()) {
      addons.push({ addon_id: x.chiave, label: x.label.trim(), ricorrenza: x.ricorrenza, tipo_valore: "importo", valore, sovrapprezzo: true });
    } else if (x.ricorrenza === "una_tantum") {
      // Sommato farebbe rinascere l'attivazione tolta: il backend lo rifiuta.
      if (!conAttivazione) errori.push(`${rinnovo ? "Nel rinnovo non c'è attivazione" : "Attivazione tolta"}: un sovrapprezzo una tantum ha bisogno di un'etichetta`);
      else attivazione += valore;
    } else {
      let quota = addons.find((a) => a.ricorrenza === "mensile" && importoFisso(a) && !a.sovrapprezzo);
      if (!quota) {
        quota = { addon_id: x.chiave, label: "Fee mensile", ricorrenza: "mensile", tipo_valore: "importo", valore: 0 };
        addons.push(quota);
      }
      quota.valore += valore;
    }
  });

  // Più sconti sulla stessa voce si sommano, ciascuno sul prezzo pieno.
  const sconta = (pieno, voce, nome) => {
    const lista = sconti.filter((x) => x.voce === voce);
    if (lista.length === 0) return null;
    const importo = arrotonda(lista.reduce((t, x) => (
      t + (x.tipo_valore === "percentuale" ? arrotonda((pieno * (Number(x.valore) || 0)) / 100) : Number(x.valore) || 0)
    ), 0));
    if (importo > pieno + 0.005) errori.push(`Lo sconto su «${nome}» supera il suo prezzo (${formattaEuro(pieno)})`);
    return { pieno, importo };
  };
  const scontoAttivazione = sconta(attivazione, VOCE_ATTIVAZIONE, nomeAttivazione(base));
  if (scontoAttivazione) attivazione = arrotonda(attivazione - scontoAttivazione.importo);
  addons.forEach((a) => {
    if (!a.diListino || !importoFisso(a)) return;
    const sconto = sconta(a.valore, a.addon_id, a.label);
    if (sconto) {
      a.sconto = sconto;
      a.valore = arrotonda(a.valore - sconto.importo);
    }
  });

  const somma = (lista) => lista.reduce((t, a) => t + a.valore, 0);
  return {
    attivazione,
    conAttivazione,
    scontoAttivazione,
    addons,
    errori,
    allaFirma: attivazione + somma(addons.filter((a) => a.ricorrenza === "una_tantum" && importoFisso(a))),
    mensile: somma(addons.filter((a) => a.ricorrenza === "mensile" && importoFisso(a))),
  };
}

// Quanto paga il cliente nel primo mese: attivazione e una tantum, più i
// canoni mensili salvo `primo_mese_solo_attivazione`, che li fa partire dal
// mese dopo (stessa regola di backend/stato_pagamenti.py). Il default acceso
// per i servizi senza il campo è quello di backend/pacchetti.py. Le quote a
// percentuale non hanno un importo noto alla firma: restano fuori dal totale.
function canoniDalSecondoMese(base, calcolo) {
  return calcolo.conAttivazione && (base.primo_mese_solo_attivazione ?? true);
}

function primoMese(base, calcolo) {
  return calcolo.allaFirma + (canoniDalSecondoMese(base, calcolo) ? 0 : calcolo.mensile);
}

// Voci su cui si può dare uno sconto: l'attivazione (non nel rinnovo, che non
// la fa pagare, e non se tolta) e le quote del listino a importo fisso rimaste.
function vociScontabili(base, s, rinnovo) {
  const conAttivazione = !rinnovo && !(s.addonRimossi || []).includes(VOCE_ATTIVAZIONE);
  const voci = conAttivazione ? [{ value: VOCE_ATTIVAZIONE, label: nomeAttivazione(base) }] : [];
  (base.addons || []).forEach((a) => {
    if (rinnovo && a.attivazione) return;
    if (importoFisso(a) && !(s.addonRimossi || []).includes(a.addon_id)) voci.push({ value: a.addon_id, label: a.label });
  });
  return voci;
}

function TotaliServizio({ base, servizio, rinnovo, testId }) {
  const listino = calcolaServizio(base, {}, rinnovo);
  const finale = calcolaServizio(base, servizio, rinnovo);
  const dalSecondoMese = canoniDalSecondoMese(base, finale);
  const totale = primoMese(base, finale);
  const totaleListino = primoMese(base, listino);
  // Voce per voce, così si vede da cosa nasce il totale del primo mese: è la
  // stessa scomposizione delle etichette sulla card del servizio.
  const voci = [
    ...(finale.conAttivazione ? [{
      chiave: VOCE_ATTIVAZIONE, label: nomeAttivazione(base), quando: "una tantum",
      valore: formattaEuro(finale.attivazione), pieno: finale.scontoAttivazione?.pieno,
    }] : []),
    ...finale.addons.map((a) => ({
      chiave: a.addon_id,
      label: a.label,
      quando: a.ricorrenza === "mensile" ? (dalSecondoMese ? "al mese, dal 2° mese" : "al mese") : "una tantum",
      valore: importoFisso(a) ? formattaEuro(a.valore) : `${a.valore}%`,
      pieno: a.sconto?.pieno,
    })),
  ];
  return (
    <div className="flex flex-col gap-1.5 text-[12.5px] leading-[1.45]" style={{ color: INK600 }} data-testid={testId}>
      <div className="flex items-baseline justify-between gap-3">
        <span className="font-semibold" style={{ color: INK900 }}>{rinnovo ? "Ogni mese" : "Primo mese"}</span>
        <span>
          {totale !== totaleListino && <span className="line-through mr-1.5" style={{ color: INK400 }}>{formattaEuro(totaleListino)}</span>}
          <strong className="contratto-font-mono text-[14px]" style={{ color: INK900 }} data-testid={`${testId}-primo-mese`}>{formattaEuro(totale)}</strong>
        </span>
      </div>
      {voci.map((v) => (
        <div key={v.chiave} className="flex items-baseline justify-between gap-3">
          <span className="min-w-0">{v.label} <span style={{ color: INK400 }}>· {v.quando}</span></span>
          <span className="shrink-0">
            {v.pieno != null && <span className="line-through mr-1.5" style={{ color: INK400 }}>{formattaEuro(v.pieno)}</span>}
            <span className="contratto-font-mono" style={{ color: INK900 }}>{v.valore}</span>
          </span>
        </div>
      ))}
      {!rinnovo && (
        <div className="flex items-baseline justify-between gap-3 pt-1.5" style={{ borderTop: "1px solid #e4e4ed" }}>
          <span>Dal 2° mese</span>
          <span>
            {finale.mensile !== listino.mensile && <span className="line-through mr-1.5" style={{ color: INK400 }}>{formattaEuro(listino.mensile)}</span>}
            <strong className="contratto-font-mono" style={{ color: INK900 }}>{formattaEuro(finale.mensile)}</strong>
            <span style={{ color: INK400 }}> al mese</span>
          </span>
        </div>
      )}
    </div>
  );
}

// Testi di pagamento scritti a mano nel catalogo senza importi collegati: il
// backend non può ricomporli (vedi personalizza_servizio). Con soli
// sovrapprezzi accoda le voci con etichetta; con voci tolte o sconti genera il
// testo dagli importi. Quelli con importi collegati (`<campo>_modello` con
// segnaposto) seguono da soli gli importi concordati: nessun avviso.
const TESTI_PAGAMENTO = ["pagamento_corrispettivo", "modalita_pagamento"];
const testoACollegato = (base, campo) => /\{\{[A-Za-z0-9_-]+\}\}/.test(base[`${campo}_modello`] || "");

function AvvisoTestiAMano({ base, servizio }) {
  const testiAMano = TESTI_PAGAMENTO.some((c) => !base[`${c}_auto`] && !testoACollegato(base, c));
  if (!testiAMano || !personalizzato(servizio)) return null;
  const rigenera = (servizio.addonRimossi || []).length > 0 || (servizio.righeSconti || []).length > 0;
  return (
    <p className="flex items-start gap-1.5 text-[12.5px] leading-[1.45]" style={{ color: "#7a4e07" }}>
      <AlertCircle className="w-3.5 h-3.5 mt-[2px] shrink-0" strokeWidth={2} />
      {rigenera
        ? "I testi di pagamento di questo servizio sono scritti a mano nel catalogo, senza importi collegati: con voci tolte o sconti il contratto li sostituisce con quelli generati dagli importi concordati."
        : "I testi di pagamento di questo servizio sono scritti a mano nel catalogo, senza importi collegati: i sovrapprezzi con etichetta si aggiungono in coda al corrispettivo, quelli senza etichetta non compaiono nel contratto."}
    </p>
  );
}

function RigheSovrapprezzi({ servizio, disabled, onChange }) {
  const righe = servizio.righeSovrapprezzi || [];
  const aggiorna = (chiave, patch) => onChange(righe.map((x) => (x.chiave === chiave ? { ...x, ...patch } : x)));
  const idServizio = servizio.servizio_id;
  return (
    <>
      {righe.map((x, i) => (
        <div key={x.chiave} className="grid grid-cols-[minmax(0,1fr)_36px] sm:grid-cols-[minmax(0,1fr)_auto_140px_36px] gap-2 items-center" data-testid={`sovrapprezzo-${idServizio}-${i}`}>
          <Input
            value={x.label}
            onChange={(e) => aggiorna(x.chiave, { label: e.target.value })}
            placeholder="Etichetta nel contratto (facoltativa)"
            disabled={disabled}
            className={FIELD_CLS}
            data-testid={`sovrapprezzo-label-${idServizio}-${i}`}
          />
          <button
            type="button"
            onClick={() => onChange(righe.filter((y) => y.chiave !== x.chiave))}
            disabled={disabled}
            aria-label="Rimuovi sovrapprezzo"
            className="sm:order-last w-[34px] h-[34px] rounded-[9px] flex items-center justify-center transition-colors hover:bg-[#fdecea] hover:text-[#c0392b] disabled:opacity-50"
            style={{ color: INK400 }}
            data-testid={`sovrapprezzo-rimuovi-${idServizio}-${i}`}
          >
            <X className="w-[15px] h-[15px]" strokeWidth={2} />
          </button>
          <SegmentedControl
            value={x.ricorrenza}
            onChange={(v) => aggiorna(x.chiave, { ricorrenza: v })}
            disabled={disabled}
            options={[
              { value: "una_tantum", label: "Una tantum", testId: `sovrapprezzo-una-tantum-${idServizio}-${i}` },
              { value: "mensile", label: "Mensile", testId: `sovrapprezzo-mensile-${idServizio}-${i}` },
            ]}
          />
          <NumberField
            value={x.valore}
            onChange={(v) => aggiorna(x.chiave, { valore: v })}
            disabled={disabled}
            placeholder="0"
            testId={`sovrapprezzo-valore-${idServizio}-${i}`}
          />
        </div>
      ))}
    </>
  );
}

function PulsanteAggiungiSovrapprezzo({ servizio, disabled, onChange }) {
  return (
    <button
      type="button"
      onClick={() => onChange([...(servizio.righeSovrapprezzi || []), { chiave: nuovaChiaveRiga("sovr"), label: "", ricorrenza: "una_tantum", valore: "" }])}
      disabled={disabled}
      className={`${GHOST_ACCENT_BTN} self-start`}
      data-testid={`sovrapprezzo-aggiungi-${servizio.servizio_id}`}
    >
      <Plus className="w-[15px] h-[15px]" strokeWidth={2.1} />
      Aggiungi sovrapprezzo
    </button>
  );
}

// Pannello sotto la card di un servizio scelto: sovrapprezzi rapidi e accesso
// alla pagina di personalizzazione completa.
function PersonalizzazioneServizio({ servizio, base, rinnovo, disabled, onChange, onPersonalizza }) {
  const righe = servizio.righeSovrapprezzi || [];
  const rimossi = [{ addon_id: VOCE_ATTIVAZIONE, label: nomeAttivazione(base) }, ...(base.addons || [])]
    .filter((a) => (servizio.addonRimossi || []).includes(a.addon_id));
  const sconti = servizio.righeSconti || [];
  const idServizio = servizio.servizio_id;
  return (
    <div
      className="mx-3 rounded-b-[12px] border border-t-0 px-3.5 pt-3 pb-3 flex flex-col gap-2.5 contratto-font-ui"
      style={{ borderColor: "#c9c9f0", background: "#fbfbfe" }}
      data-testid={`sovrapprezzi-${idServizio}`}
    >
      {righe.length > 0 && (
        <div className="flex items-center gap-1.5">
          <EtichettaCampo>Sovrapprezzi</EtichettaCampo>
          <InfoHint
            title="Sovrapprezzo"
            description="Con un'etichetta compare nel contratto come voce a sé accanto al servizio. Senza etichetta si somma all'attivazione (una tantum) o alla fee mensile (mensile) e il cliente vede solo il prezzo finale."
          />
        </div>
      )}
      <RigheSovrapprezzi servizio={servizio} disabled={disabled} onChange={(r) => onChange({ righeSovrapprezzi: r })} />
      {(rimossi.length > 0 || sconti.length > 0) && (
        <div className="flex flex-wrap items-center gap-1.5" data-testid={`personalizzazione-riassunto-${idServizio}`}>
          {rimossi.map((a) => (
            <span key={a.addon_id} className="text-[11.5px] font-medium px-2 py-0.5 rounded-full line-through" style={{ background: "#f0f0f5", color: INK600 }}>
              {a.label}
            </span>
          ))}
          {sconti.length > 0 && (
            <span className="text-[11.5px] font-semibold px-2 py-0.5 rounded-full" style={{ background: "#e7f5ec", color: SUCCESS }}>
              {sconti.length === 1 ? "1 sconto" : `${sconti.length} sconti`}
            </span>
          )}
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-1">
          <PulsanteAggiungiSovrapprezzo servizio={servizio} disabled={disabled} onChange={(r) => onChange({ righeSovrapprezzi: r })} />
          <button
            type="button"
            onClick={onPersonalizza}
            className={`${GHOST_ACCENT_BTN} self-start`}
            data-testid={`personalizza-servizio-${idServizio}`}
          >
            <SlidersHorizontal className="w-[15px] h-[15px]" strokeWidth={2.1} />
            Personalizza servizio
          </button>
        </div>
        {personalizzato(servizio) && <TotaliServizio base={base} servizio={servizio} rinnovo={rinnovo} testId={`sovrapprezzi-totali-${idServizio}`} />}
      </div>
      <AvvisoTestiAMano base={base} servizio={servizio} />
    </div>
  );
}

// Testo rich text del catalogo in sola lettura. È HTML già ripulito dal
// backend al salvataggio del servizio (testo_ricco.sanifica_html: solo
// p/h3/elenchi/grassetto, niente attributi), quindi si può inserire così com'è.
function TestoServizio({ titolo, html }) {
  if (!html) return null;
  return (
    <div className="flex flex-col gap-1.5">
      <EtichettaCampo>{titolo}</EtichettaCampo>
      <div
        className="rounded-[12px] border px-4 py-3 text-[13.5px] leading-[1.6] [&>*+*]:mt-1.5 [&_h3]:font-bold [&_h3]:text-[#15161c] [&_strong]:font-bold [&_strong]:text-[#15161c] [&_ul]:list-disc [&_ul]:pl-[22px] [&_ol]:list-decimal [&_ol]:pl-[22px] [&_li::marker]:text-[#9095a1]"
        style={{ borderColor: "#e9e9f1", background: "#fbfbfd", color: "#3c414d" }}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </div>
  );
}

function Prezzo({ valore, sconto, testId }) {
  return (
    <span className="flex flex-col items-end shrink-0" data-testid={testId}>
      {sconto && sconto.importo > 0 && (
        <span className="text-[12px] line-through contratto-font-mono" style={{ color: INK400 }}>{formattaEuro(sconto.pieno)}</span>
      )}
      <span className="text-[14.5px] font-bold contratto-font-mono" style={{ color: INK900 }}>{formattaEuro(valore)}</span>
    </span>
  );
}

// Pagina che sostituisce l'elenco dei servizi nello step 4: il servizio
// "esploso" (cosa comprende e ogni voce di prezzo) con le modifiche per il
// cliente. Si chiude dal pulsante del footer o da "Torna ai servizi".
function PaginaPersonalizzazione({ servizio, base, rinnovo, disabled, onChange, onChiudi }) {
  const calcolo = calcolaServizio(base, servizio, rinnovo);
  const rimossi = servizio.addonRimossi || [];
  const sconti = servizio.righeSconti || [];
  const voci = vociScontabili(base, servizio, rinnovo);
  const idServizio = servizio.servizio_id;
  const modificato = personalizzato(servizio);
  const perId = Object.fromEntries(calcolo.addons.map((a) => [a.addon_id, a]));

  const toggleVoce = (addonId, inclusa) => {
    const nuoviRimossi = inclusa ? rimossi.filter((x) => x !== addonId) : [...rimossi, addonId];
    // Uno sconto su una voce tolta non ha più senso: va via con lei.
    onChange({ addonRimossi: nuoviRimossi, righeSconti: inclusa ? sconti : sconti.filter((x) => x.voce !== addonId) });
  };
  const aggiornaSconto = (chiave, patch) => onChange({ righeSconti: sconti.map((x) => (x.chiave === chiave ? { ...x, ...patch } : x)) });

  return (
    <div className="flex flex-col gap-5" data-testid={`personalizzazione-${idServizio}`}>
      <div className="flex flex-col gap-2">
        <button
          type="button"
          onClick={onChiudi}
          className="self-start inline-flex items-center gap-1 text-[13px] font-semibold transition-colors hover:text-[#4a4ac4]"
          style={{ color: IND }}
          data-testid="personalizzazione-torna"
        >
          <ChevronLeft className="w-[15px] h-[15px]" strokeWidth={2.2} />
          Torna ai servizi
        </button>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[18px] font-bold" style={{ color: INK900 }} data-testid="personalizzazione-nome">
            {base.nome}{modificato ? SUFFISSO_PERSONALIZZATO : ""}
          </span>
        </div>
        {base.descrizione && <p className="text-[13.5px] leading-[1.5]" style={{ color: INK600 }}>{base.descrizione}</p>}
      </div>

      <div className="flex flex-col gap-3">
        <EtichettaSezione>Voci di prezzo</EtichettaSezione>
        <div className="flex flex-col rounded-[12px] border overflow-hidden" style={{ borderColor: "#e4e4ed" }}>
          <div
            className="flex items-center gap-3 px-4 py-3"
            style={{ background: rinnovo || calcolo.conAttivazione ? "#fff" : "#fafafc" }}
            data-testid={`personalizzazione-voce-${VOCE_ATTIVAZIONE}`}
          >
            {/* Nel rinnovo l'attivazione non è dovuta: non c'è niente da togliere. */}
            {!rinnovo && (
              <ToggleSwitch
                checked={calcolo.conAttivazione}
                onChange={(v) => toggleVoce(VOCE_ATTIVAZIONE, v)}
                disabled={disabled}
                label={`Includi ${nomeAttivazione(base)}`}
                testId={`personalizzazione-includi-${VOCE_ATTIVAZIONE}`}
              />
            )}
            <div className="flex-1 min-w-0">
              <span
                className={`text-[14px] font-semibold ${rinnovo || calcolo.conAttivazione ? "" : "line-through"}`}
                style={{ color: rinnovo || calcolo.conAttivazione ? INK900 : INK400 }}
              >
                {nomeAttivazione(base)}
              </span>
              <span className="block text-[12.5px]" style={{ color: INK400 }}>
                {rinnovo ? "Non dovuta nel rinnovo" : calcolo.conAttivazione ? "Una tantum, alla firma" : "Tolta dal servizio"}
              </span>
            </div>
            {rinnovo ? (
              <span className="text-[14.5px] font-bold contratto-font-mono" style={{ color: INK400 }}>—</span>
            ) : !calcolo.conAttivazione ? (
              <span className="text-[14.5px] font-bold contratto-font-mono line-through" style={{ color: INK400 }}>
                {formattaEuro(base.importo_attivazione)}
              </span>
            ) : (
              <Prezzo valore={calcolo.attivazione} sconto={calcolo.scontoAttivazione} testId={`personalizzazione-prezzo-attivazione-${idServizio}`} />
            )}
          </div>
          {(base.addons || []).filter((a) => !(rinnovo && a.attivazione)).map((a) => {
            const inclusa = !rimossi.includes(a.addon_id);
            const calcolata = perId[a.addon_id];
            return (
              <div
                key={a.addon_id}
                className="flex items-center gap-3 px-4 py-3 border-t"
                style={{ borderColor: "#f0f0f5", background: inclusa ? "#fff" : "#fafafc" }}
                data-testid={`personalizzazione-voce-${a.addon_id}`}
              >
                <ToggleSwitch
                  checked={inclusa}
                  onChange={(v) => toggleVoce(a.addon_id, v)}
                  disabled={disabled}
                  label={`Includi ${a.label}`}
                  testId={`personalizzazione-includi-${a.addon_id}`}
                />
                <div className="flex-1 min-w-0">
                  <span className={`text-[14px] font-semibold ${inclusa ? "" : "line-through"}`} style={{ color: inclusa ? INK900 : INK400 }}>{a.label}</span>
                  <span className="block text-[12.5px]" style={{ color: INK400 }}>
                    {inclusa ? (a.ricorrenza === "mensile" ? "Ogni mese" : "Una tantum") : "Tolta dal servizio"}
                  </span>
                </div>
                {!inclusa ? (
                  <span className="text-[14.5px] font-bold contratto-font-mono line-through" style={{ color: INK400 }}>
                    {importoFisso(a) ? formattaEuro(a.valore) : `${Number(a.valore) || 0}%`}
                  </span>
                ) : importoFisso(a) ? (
                  <Prezzo valore={calcolata.valore} sconto={calcolata.sconto} testId={`personalizzazione-prezzo-${a.addon_id}`} />
                ) : (
                  <span className="text-[14.5px] font-bold contratto-font-mono" style={{ color: INK900 }}>{Number(a.valore) || 0}% sul fatturato</span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex flex-col gap-2.5">
        <div className="flex items-center gap-1.5">
          <EtichettaSezione>Sconti</EtichettaSezione>
        </div>
        <p className="text-[12.5px] leading-[1.45]" style={{ color: INK600 }}>
          Nel contratto il cliente vede il prezzo di listino barrato, il prezzo scontato e l'importo dello sconto. Le voci tolte non compaiono.
        </p>
        {sconti.map((x, i) => (
          <div key={x.chiave} className="grid grid-cols-[minmax(0,1fr)_36px] sm:grid-cols-[minmax(0,1fr)_auto_140px_36px] gap-2 items-center" data-testid={`sconto-${idServizio}-${i}`}>
            <Select value={x.voce} onValueChange={(v) => aggiornaSconto(x.chiave, { voce: v })} disabled={disabled}>
              <SelectTrigger className={FIELD_CLS} data-testid={`sconto-voce-${idServizio}-${i}`}>
                <SelectValue placeholder="Voce da scontare" />
              </SelectTrigger>
              <SelectContent>
                {voci.map((v) => <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <button
              type="button"
              onClick={() => onChange({ righeSconti: sconti.filter((y) => y.chiave !== x.chiave) })}
              disabled={disabled}
              aria-label="Rimuovi sconto"
              className="sm:order-last w-[34px] h-[34px] rounded-[9px] flex items-center justify-center transition-colors hover:bg-[#fdecea] hover:text-[#c0392b] disabled:opacity-50"
              style={{ color: INK400 }}
              data-testid={`sconto-rimuovi-${idServizio}-${i}`}
            >
              <X className="w-[15px] h-[15px]" strokeWidth={2} />
            </button>
            <SegmentedControl
              value={x.tipo_valore}
              onChange={(v) => aggiornaSconto(x.chiave, { tipo_valore: v })}
              disabled={disabled}
              options={[
                { value: "importo", label: "Euro", testId: `sconto-euro-${idServizio}-${i}` },
                { value: "percentuale", label: "Percentuale", testId: `sconto-percentuale-${idServizio}-${i}` },
              ]}
            />
            <NumberField
              value={x.valore}
              onChange={(v) => aggiornaSconto(x.chiave, { valore: v })}
              unit={x.tipo_valore === "percentuale" ? "%" : "€"}
              disabled={disabled}
              placeholder="0"
              testId={`sconto-valore-${idServizio}-${i}`}
            />
          </div>
        ))}
        {voci.length > 0 ? (
          <button
            type="button"
            onClick={() => onChange({ righeSconti: [...sconti, { chiave: nuovaChiaveRiga("sconto"), voce: voci[0].value, tipo_valore: "importo", valore: "" }] })}
            disabled={disabled}
            className={`${GHOST_ACCENT_BTN} self-start`}
            data-testid={`sconto-aggiungi-${idServizio}`}
          >
            <Plus className="w-[15px] h-[15px]" strokeWidth={2.1} />
            Aggiungi sconto
          </button>
        ) : (
          <p className="text-[12.5px] italic" style={{ color: INK400 }}>Nessuna voce a importo fisso da scontare.</p>
        )}
      </div>

      <div className="flex flex-col gap-2.5">
        <div className="flex items-center gap-1.5">
          <EtichettaSezione>Sovrapprezzi</EtichettaSezione>
        </div>
        <p className="text-[12.5px] leading-[1.45]" style={{ color: INK600 }}>
          Con un'etichetta il sovrapprezzo compare nel contratto come voce a sé. Senza etichetta si somma all'attivazione (una tantum) o alla fee mensile (mensile) e il cliente vede solo il prezzo finale.
        </p>
        <RigheSovrapprezzi servizio={servizio} disabled={disabled} onChange={(r) => onChange({ righeSovrapprezzi: r })} />
        <PulsanteAggiungiSovrapprezzo servizio={servizio} disabled={disabled} onChange={(r) => onChange({ righeSovrapprezzi: r })} />
      </div>

      <div className="flex flex-col gap-2 rounded-[12px] px-4 py-3" style={{ background: "#f6f6fd", border: "1px solid #c9c9f0" }}>
        <TotaliServizio base={base} servizio={servizio} rinnovo={rinnovo} testId={`personalizzazione-totali-${idServizio}`} />
        {calcolo.errori.map((e) => (
          <p key={e} className="flex items-start gap-1.5 text-[12.5px] font-medium leading-[1.45]" style={{ color: DANGER }}>
            <AlertCircle className="w-3.5 h-3.5 mt-[2px] shrink-0" strokeWidth={2} />
            {e}
          </p>
        ))}
        <AvvisoTestiAMano base={base} servizio={servizio} />
      </div>

      <div className="flex flex-col gap-3">
        <EtichettaSezione>Cosa comprende</EtichettaSezione>
        <TestoServizio titolo="Piano di progetto" html={base.piano_progetto} />
        <TestoServizio titolo="Attività incluse" html={base.attivita_incluse} />
        <TestoServizio titolo="Ripartizione delle attività" html={base.ripartizione_attivita} />
      </div>
    </div>
  );
}

// Il documento d'identità è facoltativo, ma va raccolto: senza fronte e retro
// il contratto mostra un promemoria, in lista e nel wizard. Un lato mancante
// si carica anche dopo l'invio per la firma (POST /contratti/{id}/documento).
function documentoMancante(c) {
  const d = c?.documento_identita || {};
  return !d.fronte_file_id || !d.retro_file_id;
}

const SPIEGAZIONE_START_NON_PAGATO = "Ha firmato ma non ha ancora pagato la prima fattura: l'operatività parte dal pagamento, e fino ad allora il cliente non compare nella lista Clienti. Il pagamento si registra nella scheda del cliente.";

const PROMEMORIA_DOCUMENTO = "Il documento d'identità del firmatario non è ancora stato caricato: aggiungilo quanto prima.";

// ==================== Step del wizard ====================
const STEPS = [
  { key: "anagrafica", label: "Anagrafica cliente", titolo: "Anagrafica del cliente", icona: Building2 },
  { key: "legale", label: "Legale rappresentante", titolo: "Legale rappresentante o Delegato", icona: UserCircle },
  { key: "documento", label: "Documento d'identità", titolo: "Documento d'identità", icona: FileText },
  { key: "servizio", label: "Servizi", titolo: "Scelta dei servizi", icona: Package },
  { key: "durata", label: "Periodo di validità", titolo: "Periodo di validità del contratto", icona: CalendarClock },
  { key: "anteprima", label: "Anteprima", titolo: "Anteprima del contratto", icona: Eye },
];

// ==================== Sottocomponenti di presentazione ====================

function EtichettaSezione({ children }) {
  return (
    <div className="flex items-center gap-3">
      <span className="text-[11.5px] font-bold uppercase tracking-[.11em] text-[#565d6d] contratto-font-ui">{children}</span>
      <span className="flex-1 h-px bg-[#ededf3]" />
    </div>
  );
}

function EtichettaCampo({ children }) {
  return <span className="text-[12.5px] font-medium text-[#6b7280] contratto-font-ui">{children}</span>;
}

function Campo({ label, value, onChange, mono, uppercase, maxLength, inputMode, type = "text", testId, className = "" }) {
  return (
    <label className={`flex flex-col gap-1.5 ${className}`}>
      <EtichettaCampo>{label}</EtichettaCampo>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        maxLength={maxLength}
        inputMode={inputMode}
        data-testid={testId}
        className={`${INPUT_BASE} ${INPUT_NEUTRAL} ${mono ? "contratto-font-mono" : "contratto-font-ui"} ${uppercase ? "uppercase" : ""}`}
      />
    </label>
  );
}

function CampoAuto({ label, value, onChange, auto, mono, uppercase, maxLength }) {
  return (
    <label className="flex flex-col gap-1.5">
      <EtichettaCampo>{label}</EtichettaCampo>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        maxLength={maxLength}
        className={`${INPUT_BASE} ${mono ? "contratto-font-mono" : "contratto-font-ui"} ${uppercase ? "uppercase" : ""} ${auto ? "border-[#cfe7d8] bg-[#f4faf6] text-[#15161c]" : "border-[#e4e4ed] bg-white text-[#15161c]"}`}
      />
    </label>
  );
}

function CampoValidato({ label, value, onChange, stato, maxLength, uppercase, testId }) {
  return (
    <label className="flex flex-col gap-1.5">
      <EtichettaCampo>{label}</EtichettaCampo>
      <div className="relative flex">
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          maxLength={maxLength}
          data-testid={testId}
          className={`${INPUT_BASE} ${INPUT_NEUTRAL} contratto-font-mono flex-1 pr-10 ${uppercase ? "uppercase tracking-[.05em]" : ""}`}
        />
        <span className="absolute right-3 top-0 h-[42px] flex items-center" style={{ color: stato.colore }}>
          {stato.esito === "ok" && <Check className="w-[15px] h-[15px]" strokeWidth={2.2} />}
          {stato.esito === "bad" && <AlertCircle className="w-[15px] h-[15px]" strokeWidth={2.2} />}
        </span>
      </div>
      <span className="text-xs leading-[1.3] contratto-font-ui" style={{ color: stato.colore }}>{stato.messaggio}</span>
    </label>
  );
}

function CapHint({ auto, cap }) {
  return (
    <div className="flex items-center gap-1.5 text-[12.5px] leading-[1.3] contratto-font-ui" style={{ color: auto ? SUCCESS : INK400 }}>
      {auto && <Check className="w-3.5 h-3.5" strokeWidth={2} />}
      {auto ? `Comune e provincia compilati dal CAP ${cap}` : "Inserisci il CAP per compilare comune e provincia"}
    </div>
  );
}

// Dropdown "Tipo documento" — componente bespoke (non un primitivo generico):
// si apre VERSO L'ALTO perché il campo sta nell'ultima sezione dello step e la
// card della modale ha overflow:hidden — aprendosi in basso il pannello verrebbe
// tagliato. Chiusura su click esterno o Escape.
//
// "aperto" è controllato dal genitore (non stato locale): Radix Dialog
// intercetta Escape con un listener in fase di cattura su `document`,
// registrato quando la modale si apre — arriva SEMPRE prima di qualsiasi
// listener o onKeyDown React aggiunto dopo, quindi chiuderebbe l'intera
// modale invece del solo pannello. L'unico modo per evitarlo è l'API di
// Radix pensata apposta per questo (`onEscapeKeydown` + `preventDefault()`
// su DialogContent), che richiede che il genitore sappia se il dropdown è
// aperto — da qui il sollevamento dello stato.
function DropdownDocumento({ value, onChange, aperto, onApertoChange, testId }) {
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!aperto) return;
    const chiudiSeFuori = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) onApertoChange(false);
    };
    document.addEventListener("pointerdown", chiudiSeFuori, true);
    return () => {
      document.removeEventListener("pointerdown", chiudiSeFuori, true);
    };
  }, [aperto, onApertoChange]);

  const selezionato = DOCUMENTI.find((d) => d.valore === value);
  const IconaTrigger = selezionato?.Icona;

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        onClick={() => onApertoChange(!aperto)}
        className="w-full h-[42px] pl-3 pr-2.5 flex items-center gap-2.5 rounded-[10px] border bg-white text-left transition-colors contratto-font-ui"
        style={{ borderColor: aperto ? IND : "#e4e4ed", boxShadow: aperto ? "0 0 0 3px rgba(91,91,214,.14)" : "none" }}
        onMouseEnter={(e) => { if (!aperto) e.currentTarget.style.borderColor = "#c9c9f0"; }}
        onMouseLeave={(e) => { if (!aperto) e.currentTarget.style.borderColor = "#e4e4ed"; }}
        aria-expanded={aperto}
        data-testid={testId}
      >
        <span className="w-[26px] h-[26px] rounded-[7px] flex items-center justify-center shrink-0" style={{ background: "#f1f1fb", color: IND }}>
          {IconaTrigger ? <IconaTrigger /> : <FileText className="w-[15px] h-[15px]" strokeWidth={1.8} style={{ color: INK400 }} />}
        </span>
        <span className={`flex-1 min-w-0 text-[14.5px] font-semibold truncate ${selezionato ? "" : "font-medium"}`} style={{ color: selezionato ? INK900 : INK400 }}>
          {selezionato?.titolo || "Seleziona documento"}
        </span>
        <span className="shrink-0 flex transition-transform duration-200" style={{ color: "#8b8fa8", transform: aperto ? "rotate(180deg)" : "none" }}>
          <ChevronDown className="w-4 h-4" />
        </span>
      </button>

      {aperto && (
        <div
          className="dropdown-pop-anim absolute z-30 p-1.5 flex flex-col gap-0.5 rounded-[13px] border bg-white"
          style={{
            bottom: "calc(100% + 7px)",
            left: 0,
            right: 0,
            borderColor: "#e9e9f1",
            boxShadow: "0 1px 2px rgba(20,22,35,.05), 0 -18px 40px -14px rgba(20,22,35,.28)",
          }}
        >
          {DOCUMENTI.map((d) => {
            const Icona = d.Icona;
            const selezionata = d.valore === value;
            return (
              <button
                key={d.valore}
                type="button"
                onClick={() => { onChange(d.valore); onApertoChange(false); }}
                className="flex items-center gap-2.5 px-[9px] py-2 rounded-[9px] text-left transition-colors contratto-font-ui"
                style={{ background: selezionata ? "#f6f6fd" : "transparent" }}
                onMouseEnter={(e) => { if (!selezionata) e.currentTarget.style.background = "#f4f4fc"; }}
                onMouseLeave={(e) => { if (!selezionata) e.currentTarget.style.background = selezionata ? "#f6f6fd" : "transparent"; }}
              >
                <span className="w-7 h-7 rounded-[8px] flex items-center justify-center shrink-0" style={{ background: "#f1f1fb", color: IND }}>
                  <Icona />
                </span>
                <span className="flex-1 min-w-0 flex flex-col gap-0.5">
                  <span className="text-sm font-semibold" style={{ color: INK900 }}>{d.titolo}</span>
                  <span className="text-[11.5px] font-medium" style={{ color: INK400 }}>{d.descrizione}</span>
                </span>
                <span className="shrink-0 flex" style={{ color: IND, opacity: selezionata ? 1 : 0 }}>
                  <Check className="w-4 h-4" strokeWidth={2.4} />
                </span>
              </button>
            );
          })}
          {/* Il tipo è facoltativo: una scelta fatta per errore si toglie da qui. */}
          {value && (
            <button
              type="button"
              onClick={() => { onChange(""); onApertoChange(false); }}
              className="px-[9px] py-2 rounded-[9px] text-left text-[12.5px] font-semibold transition-colors contratto-font-ui hover:bg-[#f4f4fc]"
              style={{ color: INK400 }}
              data-testid={testId ? `${testId}-nessuno` : undefined}
            >
              Non indicare il documento
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function StepperContratto({ stepIndex, maxStepReached, onVai }) {
  return (
    <div className="flex gap-3.5" data-testid="contratto-stepper">
      {STEPS.map((s, i) => {
        const attivoOCompletato = i <= stepIndex;
        const sbloccato = i <= maxStepReached;
        return (
          <button
            key={s.key}
            type="button"
            onClick={() => sbloccato && onVai(i)}
            disabled={!sbloccato}
            className={`flex-1 flex flex-col gap-2 text-left bg-transparent border-none p-0 ${sbloccato ? "cursor-pointer" : "cursor-not-allowed"}`}
            data-testid={`stepper-${s.key}`}
          >
            <span
              className="block h-[3px] rounded-full transition-colors duration-[400ms]"
              style={{ background: attivoOCompletato ? IND : "#eceef3" }}
            />
            <span className="flex items-center gap-2">
              <span className="text-[12.5px] font-semibold contratto-font-mono" style={{ color: attivoOCompletato ? INK900 : INK400 }}>
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="text-[13px] font-semibold contratto-font-ui" style={{ color: attivoOCompletato ? INK900 : INK400 }}>
                {s.label}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

// Dispositivo con puntatore impreciso (telefono, tablet): lì il documento si fotografa
// sul momento, non si trascina. Letto una volta al montaggio: non cambia durante l'uso.
function isDispositivoTouch() {
  return typeof window !== "undefined" && !!window.matchMedia?.("(pointer: coarse)").matches;
}

// Le foto dei telefoni recenti (48-200MP) superano facilmente i 10MB del backend e
// sono lente da caricare in mobilità. Per leggere un documento bastano 2400px sul lato
// lungo: si ridimensiona e ricomprime in JPEG. Se il browser non riesce a decodificare
// l'immagine si restituisce il file originale e decide la validazione a valle.
const LATO_MAX_FOTO_PX = 2400;
async function ridimensionaFoto(file) {
  if (!file.type.startsWith("image/")) return file;
  try {
    const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
    const scala = Math.min(1, LATO_MAX_FOTO_PX / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bitmap.width * scala);
    canvas.height = Math.round(bitmap.height * scala);
    canvas.getContext("2d").drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close?.();
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.88));
    if (!blob) return file;
    const nome = (file.name || "foto").replace(/\.[^.]+$/, "") + ".jpg";
    return new File([blob], nome, { type: "image/jpeg" });
  } catch {
    return file;
  }
}

// `bloccato`: contratto già inviato per la firma, un lato caricato non si
// sostituisce più (si può solo completare quello mancante).
function DocumentoCard({ lato, documento, uploading, onSelectFile, onRimuovi, bloccato = false, testId }) {
  const inputRef = useRef(null);
  const cameraRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [touch] = useState(isDispositivoTouch);
  const caricata = !!documento;

  return (
    <div
      className={`flex flex-col gap-2.5 p-3.5 rounded-[13px] ${
        caricata
          ? "border border-[#e8f3ec] bg-[#f8fcfa]"
          : `border border-dashed ${dragOver ? "border-[#5b5bd6] bg-[#f6f6fd]" : "border-[#d9d9e6] bg-[#fafafc]"}`
      }`}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        const file = e.dataTransfer.files?.[0];
        if (file) onSelectFile(file);
      }}
    >
      <div className="flex items-center justify-between">
        <span
          className="text-[11px] font-bold uppercase contratto-font-mono"
          style={{ color: caricata ? SUCCESS : "#8b8fa8" }}
        >
          {lato}
        </span>
        {caricata ? (
          <span className="text-[11.5px] font-medium contratto-font-mono" style={{ color: SUCCESS }}>
            {formattaDimensione(documento.size)}
          </span>
        ) : (
          <span className="text-[11.5px] font-medium contratto-font-mono" style={{ color: INK400 }}>
            facoltativo
          </span>
        )}
      </div>

      <div
        className={`h-[180px] rounded-[10px] flex items-center justify-center overflow-hidden ${caricata ? "" : "cursor-pointer"}`}
        style={!caricata ? { background: "repeating-linear-gradient(135deg,#f4f4f8 0 8px,#eeeef4 8px 16px)" } : undefined}
        onClick={() => !caricata && (touch ? cameraRef : inputRef).current?.click()}
        data-testid={testId}
      >
        {uploading ? (
          <Loader2 className="w-6 h-6 animate-spin" style={{ color: IND }} />
        ) : caricata ? (
          documento.contentType?.startsWith("image/") ? (
            <img src={documento.url} alt={lato} className="w-full h-full object-cover" />
          ) : (
            <div className="flex flex-col items-center gap-1.5 text-[11.5px] font-medium contratto-font-mono" style={{ color: SUCCESS }}>
              <FileText className="w-6 h-6" />
              {documento.filename}
            </div>
          )
        ) : (
          <div className="flex flex-col items-center gap-2">
            {touch ? <Camera className="w-5 h-5" style={{ color: "#9095a1" }} /> : <Upload className="w-5 h-5" style={{ color: "#9095a1" }} />}
            <span className="text-[11.5px] font-medium contratto-font-mono" style={{ color: "#9095a1" }}>
              {touch ? "fotografa" : "trascina qui"} {lato === "FRONTE" ? "il fronte" : "il retro"}
            </span>
          </div>
        )}
      </div>

      {caricata && bloccato ? null : caricata ? (
        <div className="flex gap-2">
          {touch && (
            <button
              type="button"
              onClick={() => cameraRef.current?.click()}
              className="flex-1 h-[34px] rounded-[9px] border border-[#cfe7d8] bg-white text-[12.5px] font-medium contratto-font-ui hover:bg-[#f0f8f4] transition-colors inline-flex items-center justify-center gap-1.5"
              style={{ color: SUCCESS }}
              data-testid={`${testId}-riscatta`}
            >
              <Camera className="w-3.5 h-3.5" />
              Riscatta
            </button>
          )}
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="flex-1 h-[34px] rounded-[9px] border border-[#cfe7d8] bg-white text-[12.5px] font-medium contratto-font-ui hover:bg-[#f0f8f4] transition-colors"
            style={{ color: SUCCESS }}
          >
            Sostituisci
          </button>
          <button
            type="button"
            onClick={onRimuovi}
            className="flex-1 h-[34px] rounded-[9px] border border-[#f0dada] bg-white text-[12.5px] font-medium contratto-font-ui hover:bg-[#fdf5f5] transition-colors"
            style={{ color: DANGER }}
          >
            Rimuovi
          </button>
        </div>
      ) : touch ? (
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => cameraRef.current?.click()}
            className="flex-1 h-[34px] rounded-[9px] border-none text-[12.5px] font-semibold text-white contratto-font-ui inline-flex items-center justify-center gap-1.5"
            style={{ background: IND }}
            data-testid={`${testId}-scatta`}
          >
            <Camera className="w-3.5 h-3.5" />
            Scatta foto
          </button>
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="flex-1 h-[34px] rounded-[9px] border border-[#d9d9e6] bg-white text-[12.5px] font-medium contratto-font-ui"
            style={{ color: INK600 }}
          >
            Scegli file
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="h-[34px] rounded-[9px] border-none text-[12.5px] font-semibold text-white contratto-font-ui transition-colors"
          style={{ background: IND }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "#4c4cc4")}
          onMouseLeave={(e) => (e.currentTarget.style.background = IND)}
        >
          Scegli file
        </button>
      )}

      {/* capture="environment" apre direttamente la fotocamera posteriore; senza,
          iOS e Android mostrerebbero prima il selettore file/galleria. */}
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={async (e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          if (file) onSelectFile(await ridimensionaFoto(file));
        }}
      />

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,application/pdf"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onSelectFile(file);
          e.target.value = "";
        }}
      />
    </div>
  );
}

// Stato di un contratto in lista: fonde `stato` (bozza/completato) e
// `stato_firma` (non_inviato/in_attesa/firmato/annullato, aggiunti dal modulo
// firma) in un'unica indicazione, senza introdurre un terzo campo persistito.
// Il tono accento segna ciò che aspetta noi, il warning ciò che aspetta il cliente.
function statoLista(c) {
  if (c.stato !== "completato") return { chiave: "bozza", testo: "Bozza", tone: "neutral" };
  const statoFirma = c.stato_firma || "non_inviato";
  if (statoFirma === "firmato") return { chiave: "firmato", testo: "Firmato", tone: "success" };
  if (statoFirma === "in_attesa") return { chiave: "in_attesa", testo: "In attesa di firma", tone: "warning" };
  if (statoFirma === "annullato") return { chiave: "annullato", testo: "Invio annullato", tone: "neutral" };
  return { chiave: "da_inviare", testo: "Da inviare", tone: "accent" };
}

// Tipo del contratto nel "contenitore" di un cliente (vedi il docstring di
// backend/routes/contratti.py): il primo crea il cliente alla firma, i
// successivi si aggiungono ai contratti in essere o ne rinnovano uno.
const ETICHETTE_TIPO = {
  primo: "Primo contratto",
  aggiuntivo: "Contratto aggiuntivo",
  rinnovo: "Rinnovo",
  regolarizzazione: "Regolarizzazione",
};

const SPIEGAZIONI_TIPO = {
  primo: "Alla firma crea il cliente. Da lì si possono aggiungere contratti aggiuntivi e rinnovi.",
  aggiuntivo: "Servizi in più per un cliente esistente, con la loro attivazione e una scadenza propria.",
  rinnovo: "Sostituisce il contratto precedente dalla sua scadenza, senza una nuova attivazione.",
  regolarizzazione: "Primo contratto nell'app di un cliente che esisteva già: riprende i servizi della scheda, senza una nuova attivazione.",
};

// Per il backend una regolarizzazione è un rinnovo senza contratto rinnovato
// (regolarizzabile in routes/contratti.py): cambia solo come la si chiama.
const chiaveTipo = (c) => (c?.regolarizzazione ? "regolarizzazione" : c?.tipo);

// Spiegazione del badge di stato (vedi etichettaStato).
const SPIEGAZIONI_STATO = {
  Bozza: "Wizard non completato: usa Riprendi per finirlo.",
  "Da inviare": "Completato ma non ancora inviato al cliente: usa Invia per la firma.",
  "In attesa di firma": "Il cliente ha ricevuto il link di firma. Puoi reinviarlo o annullare l'invio.",
  "Invio annullato": "Il link di firma non è più valido: il contratto è di nuovo modificabile.",
  Firmato: "Firmato dal cliente e applicato alla sua scheda.",
};

// Perché un servizio non è selezionabile in un contratto aggiuntivo o in un
// rinnovo: il cliente lo ha già, e un servizio compare una volta sola per
// cliente (backend/pacchetti.py, servizi_gia_attivi). `provenienza` arriva da
// GET /clients/{id}/contratti/precompila.
function motivoServizioNonDisponibile(provenienza) {
  const scadenza = provenienza?.data_scadenza
    ? ` (scade il ${new Date(provenienza.data_scadenza).toLocaleDateString("it-IT")})`
    : "";
  if (provenienza?.origine === "contratto") {
    const nome = { primo: "il primo contratto", aggiuntivo: "un contratto aggiuntivo", rinnovo: "un rinnovo" }[provenienza.tipo] || "un altro contratto";
    return `Il cliente ha già questo servizio con ${nome}${scadenza}. Per modificarlo o prolungarlo, rinnova quel contratto.`;
  }
  if (provenienza?.origine === "pregresso") {
    return `Il cliente ha già questo servizio con il contratto in corso precedente all'app${scadenza}. Per prolungarlo, rinnova quel contratto.`;
  }
  if (provenienza?.origine === "scheda") {
    return "Il cliente ha già questo servizio, aggiunto dalla scheda cliente. Toglilo da lì se deve entrare in questo contratto.";
  }
  return "Il cliente ha già questo servizio da un altro contratto.";
}

// Contratti raggruppati per cliente, gruppi nell'ordine dell'elenco (più
// recente prima) e contratti del gruppo in ordine cronologico, così si legge
// la storia del cliente: primo contratto, poi aggiuntivi e rinnovi. Un primo
// contratto non ancora firmato non ha cliente e fa gruppo da sé.
function raggruppaPerCliente(contratti) {
  const gruppi = new Map();
  contratti.forEach((c) => {
    const chiave = c.cliente_id || `contratto:${c.contratto_id}`;
    if (!gruppi.has(chiave)) gruppi.set(chiave, { chiave, clienteId: c.cliente_id || null, contratti: [] });
    gruppi.get(chiave).contratti.push(c);
  });
  return [...gruppi.values()].map((g) => {
    const ordinati = [...g.contratti].sort((a, b) => String(a.created_at).localeCompare(String(b.created_at)));
    const ultimo = ordinati[ordinati.length - 1];
    return { ...g, contratti: ordinati, nome: ultimo.anagrafica?.ragione_sociale || "—" };
  });
}

// ==================== Lista "Contratti creati" (design handoff "Contratti creati — accordion", opzione 3a) ====================
// A differenza del wizard, la lista usa il design system dell'app
// (designSystem.jsx, .scheda-font-*): è una superficie di lavoro come le altre,
// non il modulo con identità propria.

const GIORNO_MS = 1000 * 60 * 60 * 24;
// Durata dell'uscita di una riga eliminata: allineata alle transizioni
// .contratti-acc-*[data-uscita] in index.css.
const DURATA_USCITA_MS = 450;

// Tile iniziali del cliente: a rotazione su tre toni di ICON_TILES, scelti da
// un hash del nome così che un cliente non cambi colore quando la lista si
// filtra o si riordina.
const TILE_CLIENTE = [ICON_TILES.viola, ICON_TILES.teal, ICON_TILES.arancio];
const PAROLE_NON_SIGNIFICATIVE = new Set(["srl", "s.r.l.", "srls", "s.r.l.s.", "spa", "s.p.a.", "snc", "s.n.c.", "sas", "s.a.s.", "di", "e", "&", "il", "lo", "la", "i", "gli", "le"]);

function tileCliente(nome) {
  let h = 0;
  for (const ch of String(nome)) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return TILE_CLIENTE[h % TILE_CLIENTE.length];
}

function inizialiCliente(nome) {
  const parole = String(nome || "")
    .split(/\s+/)
    .filter((p) => /[a-z0-9]/i.test(p) && !PAROLE_NON_SIGNIFICATIVE.has(p.toLowerCase()));
  return parole.slice(0, 2).map((p) => p.match(/[a-z0-9]/i)[0].toUpperCase()).join("") || "—";
}

const giorniFmt = (n) => Math.abs(n).toLocaleString("it-IT");
const dataBreve = (d) => d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit" });

const daInviare = (c) => c.stato === "completato" && ["non_inviato", "annullato"].includes(c.stato_firma || "non_inviato");
// Stessa condizione del vecchio bottone "Rinnova": un rinnovo per contratto.
const rinnovabile = (c) => c.stato_firma === "firmato" && !!c.cliente_id && !c.rinnovato_da;
// Rinnovo aperto (ultimo mese, FINESTRA_RINNOVO_MESI): diventa l'azione principale.
const daRinnovareOra = (c) => rinnovabile(c) && !!c.data_scadenza && rinnovoAperto(c.data_scadenza);
// Aperti di default i clienti con qualcosa da fare: chi apre la pagina cerca quelli.
const richiedeAzione = (c) => c.stato === "bozza" || daInviare(c) || c.stato_firma === "in_attesa" || daRinnovareOra(c);

// Scadenza più vicina fra i contratti firmati ancora in corso e non già
// rinnovati: è quella su cui il cliente va richiamato.
function prossimaScadenza(contratti) {
  return contratti
    .filter((c) => c.stato_firma === "firmato" && !c.rinnovato_da)
    .map((c) => statoScadenza(c.data_inizio, c.data_scadenza))
    .filter((s) => s && (s.tono === "ok" || s.tono === "urgente"))
    .sort((a, b) => a.giorni - b.giorni)[0] || null;
}

// Riepilogo stati della testata: un badge per stato presente, nell'ordine
// in cui compaiono nei contratti del cliente.
function riepilogoStati(contratti) {
  const conteggi = new Map();
  contratti.forEach((c) => {
    const st = statoLista(c);
    const voce = conteggi.get(st.chiave) || { ...st, n: 0 };
    voce.n += 1;
    conteggi.set(st.chiave, voce);
  });
  return [...conteggi.values()];
}

// Colonna centrale della riga: per un firmato la barra del periodo, per gli
// altri una riga di testo su quando partirà o da quanto aspetta la firma.
function ColonnaScadenza({ c }) {
  const s = c.stato_firma === "firmato" ? statoScadenza(c.data_inizio, c.data_scadenza) : null;
  if (!s) {
    let testo;
    if (c.stato_firma === "in_attesa") {
      const inviato = c.firma?.inviato_at || c.firma?.token_creato_at;
      const gg = inviato ? Math.floor((Date.now() - new Date(inviato)) / GIORNO_MS) : null;
      testo = gg == null ? "In attesa della firma" : gg === 0 ? "Inviato oggi" : `Inviato ${giorniFmt(gg)} gg fa`;
    } else {
      const mesi = c.durata_mesi ? ` · ${c.durata_mesi} ${c.durata_mesi === 1 ? "mese" : "mesi"}` : "";
      testo = `Parte alla firma${mesi}`;
    }
    return <span className="text-[12px] text-[#9095a1]">{testo}</span>;
  }
  const rinnovato = !!c.rinnovato_da;
  const [etichetta, colore, barra] = s.tono === "futuro"
    ? [`decorre tra ${giorniFmt(s.giorniAlInizio)} gg`, "#5b5bd6", "#c9c9f0"]
    : s.tono === "scaduto" ? [`scaduto da ${giorniFmt(s.giorni)} gg`, "#c0392b", "#c0392b"]
    : s.tono === "urgente" ? [s.giorni === 0 ? "scade oggi" : `tra ${giorniFmt(s.giorni)} gg`, "#b45309", "#f59e0b"]
    : [`tra ${giorniFmt(s.giorni)} gg`, "#565d6d", "#5b5bd6"];
  return (
    <InfoTooltip title="Periodo del contratto" description={descrizionePeriodo(c.data_inizio, c.data_scadenza, rinnovato)} block>
      <div className="w-full flex flex-col gap-[5px]" data-testid={`scadenza-contratto-${c.contratto_id}`}>
        <div className="flex items-baseline justify-between gap-2">
          <span className="scheda-font-mono text-[12px] text-[#565d6d]">{dataBreve(s.fine)}</span>
          <span className="text-[12px] font-semibold" style={{ color: rinnovato ? "#9095a1" : colore }}>
            {rinnovato ? "rinnovato" : etichetta}
          </span>
        </div>
        <div className="h-1 rounded-[2px] bg-[#e9e9f1] overflow-hidden">
          <div className="h-1 rounded-[2px]" style={{ width: `${Math.round(s.trascorso * 100)}%`, background: rinnovato ? "#c3c6cf" : barra }} />
        </div>
      </div>
    </InfoTooltip>
  );
}

const MENU_ITEM_CLS = "h-[34px] px-2.5 rounded-[8px] text-[13px] font-medium cursor-pointer scheda-font-ui focus:bg-[#f3f3f8]";

// Un contratto: identità, scadenza, un'azione principale che dipende dallo
// stato e le altre nel menu ⋯. I data-testid sono quelli dei vecchi bottoni,
// spostati sulle voci equivalenti.
function RigaContratto({ c, primo, azioni, caricandoPdfId, scaricandoPdfFirmatoId, annullandoFirmaId }) {
  const id = c.contratto_id;
  const stato = statoLista(c);
  const tipo = ETICHETTE_TIPO[chiaveTipo(c)] || ETICHETTE_TIPO.primo;
  const serviziNomi = (c.servizi || []).map((x) => x.nome).filter(Boolean);
  const legaleNome = [c.legale_rappresentante?.nome, c.legale_rappresentante?.cognome].filter(Boolean).join(" ");
  const autore = c.created_by ? c.created_by_name || "Utente rimosso" : null;

  const voceVediPdf = { label: "Vedi PDF", onSelect: () => azioni.vediPdf(c), testId: `pdf-contratto-${id}`, disabled: caricandoPdfId === id };
  const vocePdfFirmato = { label: "PDF firmato", onSelect: () => azioni.scaricaPdfFirmato(c), testId: `pdf-firmato-${id}`, disabled: scaricandoPdfFirmatoId === id };
  const voceScheda = (label) => ({ label, onSelect: () => azioni.apriScheda(c), testId: `scheda-contratto-${id}` });
  // Per una bozza "Riprendi" porta già al wizard, dove c'è il promemoria.
  const voceDocumento = documentoMancante(c) && stato.chiave !== "bozza"
    ? { label: "Carica documento", onSelect: () => azioni.apriScheda(c, "documento"), testId: `carica-documento-${id}` }
    : null;
  const voceCliente = c.cliente_id ? { label: "Vai al cliente", to: `/scheda-cliente/${c.cliente_id}`, testId: `vai-cliente-${id}` } : null;
  const rinnova = () => azioni.rinnova(c);

  let primaria;
  let menu;
  if (stato.chiave === "bozza") {
    primaria = { label: "Riprendi", onClick: () => azioni.riprendi(c), testId: `riprendi-contratto-${id}` };
    menu = [];
  } else if (daInviare(c)) {
    primaria = { label: "Invia per la firma", onClick: () => azioni.inviaFirma(c), testId: `invia-firma-${id}`, accento: true };
    menu = [voceVediPdf, voceScheda("Modifica scheda")];
  } else if (stato.chiave === "in_attesa") {
    primaria = { label: "Reinvia", onClick: () => azioni.inviaFirma(c), testId: `reinvia-firma-${id}` };
    menu = [
      { label: "Copia link di firma", onSelect: () => azioni.copiaLinkFirma(c), testId: `copia-link-firma-${id}` },
      voceVediPdf,
      voceScheda("Vedi scheda"),
      { label: "Annulla e modifica", onSelect: () => azioni.annullaInvio(c), testId: `annulla-firma-${id}`, danger: true, disabled: annullandoFirmaId === id },
    ];
  } else if (daRinnovareOra(c)) {
    primaria = { label: "Rinnova", onClick: rinnova, testId: `rinnova-contratto-${id}`, accento: true };
    menu = [vocePdfFirmato, voceCliente, voceScheda("Vedi scheda"), voceVediPdf];
  } else {
    primaria = { label: "PDF firmato", onClick: vocePdfFirmato.onSelect, testId: vocePdfFirmato.testId, caricando: scaricandoPdfFirmatoId === id };
    // Fuori dalla finestra di rinnovo la voce resta, inattiva, e dice da quando si apre.
    const apertura = c.data_scadenza ? aperturaRinnovo(c.data_scadenza) : null;
    const voceRinnova = rinnovabile(c)
      ? { label: apertura ? `Rinnova · dal ${apertura.toLocaleDateString("it-IT")}` : "Rinnova", onSelect: rinnova, testId: `rinnova-contratto-${id}`, disabled: !!apertura }
      : null;
    menu = [voceCliente, voceScheda("Vedi scheda"), voceVediPdf, voceRinnova];
  }
  menu = [voceDocumento, ...menu].filter(Boolean);
  // Un'azione del menu in corso (il menu si è già chiuso): lo spinner va sul ⋯.
  const menuOccupato = [caricandoPdfId, annullandoFirmaId, primaria.testId.startsWith("pdf-firmato") ? null : scaricandoPdfFirmatoId].includes(id);

  return (
    <div
      className={`relative grid grid-cols-1 gap-3 py-4 pr-4 pl-4 items-center md:grid-cols-[minmax(0,1fr)_190px_250px] md:gap-5 md:pl-[66px] ${primo ? "" : "border-t border-[#eeeef4]"}`}
      data-testid={`contratto-row-${id}`}
    >
      <div className="flex flex-col gap-[7px] min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <InfoTooltip title={tipo} description={SPIEGAZIONI_TIPO[chiaveTipo(c)] || SPIEGAZIONI_TIPO.primo}>
            <span className="text-[14px] font-semibold text-[#15161c]">{tipo}</span>
          </InfoTooltip>
          <InfoTooltip title={stato.testo} description={SPIEGAZIONI_STATO[stato.testo]}>
            <Badge tone={stato.tone} dot testId={`stato-firma-${id}`}>{stato.testo}</Badge>
          </InfoTooltip>
          {documentoMancante(c) && (
            <InfoTooltip title="Documento mancante" description={PROMEMORIA_DOCUMENTO}>
              <button
                type="button"
                onClick={() => (stato.chiave === "bozza" ? azioni.riprendi(c) : azioni.apriScheda(c, "documento"))}
                data-testid={`documento-mancante-${id}`}
              >
                <Badge tone="warning" dot>Documento mancante</Badge>
              </button>
            </InfoTooltip>
          )}
          {c.cliente_in_attesa_avvio && (
            <InfoTooltip
              title="Start non pagato"
              description={SPIEGAZIONE_START_NON_PAGATO}
            >
              <Link to={`/scheda-cliente/${c.cliente_id}`} data-testid={`start-non-pagato-${id}`}>
                <Badge tone="danger" dot>Firmato, start non pagato</Badge>
              </Link>
            </InfoTooltip>
          )}
          {c.rinnovato_da && (
            <InfoTooltip title="Rinnovato" description="Ha già un rinnovo: alla scadenza prosegue con quello. Non si può rinnovare una seconda volta.">
              <Badge tone="neutral">Rinnovato</Badge>
            </InfoTooltip>
          )}
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {/* Senza servizi (bozza ferma prima dello step servizi) un testo, non
              un chip: un chip con la P.IVA si leggerebbe come un servizio. */}
          {serviziNomi.length > 0
            ? serviziNomi.map((nome) => <Chip key={nome} size="sm">{nome}</Chip>)
            : <span className="text-[12px] italic text-[#9095a1]">Nessun servizio scelto</span>}
          {(legaleNome || autore) && (
            <span className="text-[12px] text-[#9095a1] ml-1 whitespace-nowrap">
              {legaleNome}{legaleNome && autore ? " · " : ""}{autore ? `di ${autore}` : ""}
            </span>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-[5px]">
        <ColonnaScadenza c={c} />
      </div>

      <div className="flex items-center gap-2 justify-start md:justify-end">
        <button
          type="button"
          onClick={primaria.onClick}
          disabled={primaria.caricando}
          className={primaria.accento ? PRIMARY_BTN_SM : SECONDARY_BTN}
          data-testid={primaria.testId}
        >
          {primaria.caricando && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          {primaria.label}
        </button>
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label="Altre azioni"
              className="w-8 h-8 shrink-0 rounded-[10px] border border-[#e0e0ea] bg-white hover:border-[#c9c9f0] data-[state=open]:bg-[#f3f3f8] transition-colors flex items-center justify-center"
              data-testid={`contratto-menu-${id}`}
            >
              {menuOccupato
                ? <Loader2 className="w-4 h-4 text-[#565d6d] animate-spin" />
                : <MoreHorizontal className="w-4 h-4 text-[#565d6d]" strokeWidth={1.8} />}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="w-[210px] rounded-[14px] border-[#e9e9f1] bg-white p-1.5 shadow-[0_14px_36px_rgba(15,23,42,.12)] scheda-font-ui flex flex-col gap-px"
          >
            {menu.map((v) => (
              <DropdownMenuItem
                key={v.testId}
                asChild={!!v.to}
                disabled={v.disabled}
                onSelect={v.onSelect}
                className={`${MENU_ITEM_CLS} ${v.danger ? "text-[#c0392b] focus:text-[#c0392b]" : "text-[#3c414d] focus:text-[#3c414d]"}`}
                data-testid={v.testId}
              >
                {v.to ? <Link to={v.to}>{v.label}</Link> : v.label}
              </DropdownMenuItem>
            ))}
            {menu.length > 0 && <DropdownMenuSeparator className="mx-0 my-1 bg-[#f0f0f6]" />}
            <DropdownMenuItem
              onSelect={() => azioni.elimina(c)}
              className={`${MENU_ITEM_CLS} text-[#c0392b] focus:text-[#c0392b]`}
              data-testid={`elimina-contratto-${id}`}
            >
              Elimina
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

// Accordion di un cliente. `contratti` sono quelli che passano i filtri,
// `tutti` quelli del cliente: testata, conteggi e riepilogo stati restano sul
// totale, così filtrare non fa sembrare il cliente diverso da com'è.
// `inUscita`: contratti appena eliminati, che restano a schermo il tempo
// dell'animazione di uscita (vedi confermaElimina). Se escono tutti quelli
// visibili del cliente, esce l'intero accordion.
function GruppoContratti({ gruppo, tutti, aperto, onToggle, onAggiuntivo, rigaProps, inUscita = {} }) {
  const idGruppo = gruppo.clienteId || gruppo.contratti[0].contratto_id;
  const tile = tileCliente(gruppo.nome);
  const scadenza = prossimaScadenza(tutti);
  const n = tutti.length;
  // Corpo montato alla prima apertura e poi tenuto (inert da chiuso), così si
  // anima anche la chiusura. Alla prima apertura il browser deve aver calcolato
  // lo stato chiuso prima di espandere, altrimenti la transizione non parte: la
  // lettura di offsetHeight forza quel calcolo (niente requestAnimationFrame,
  // che nelle schede in background resta sospeso).
  const corpoRef = useRef(null);
  const [montato, setMontato] = useState(aperto);
  const [espanso, setEspanso] = useState(aperto);
  useEffect(() => {
    if (!aperto) { setEspanso(false); return; }
    if (!montato) { setMontato(true); return; }
    void corpoRef.current?.offsetHeight;
    setEspanso(true);
  }, [aperto, montato]);
  const toggleDaTastiera = (e) => {
    if (e.target !== e.currentTarget) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onToggle();
    }
  };
  const gruppoInUscita = gruppo.contratti.every((c) => inUscita[c.contratto_id]);
  return (
    <div className="contratti-acc-guscio" data-uscita={gruppoInUscita}>
    <div className="contratti-acc rounded-[14px] border border-[#e9e9f1] bg-white" data-aperto={espanso} data-testid={`contratti-gruppo-${idGruppo}`}>
      <div
        role="button"
        tabIndex={0}
        aria-expanded={aperto}
        onClick={onToggle}
        onKeyDown={toggleDaTastiera}
        className="flex items-center gap-3 px-4 py-3.5 flex-wrap cursor-pointer rounded-[14px] hover:bg-[#fbfbfd] transition-colors outline-none focus-visible:shadow-[0_0_0_3px_rgba(91,91,214,.14)]"
        data-testid={`contratti-gruppo-toggle-${idGruppo}`}
      >
        <div className="contratti-acc-tile w-[38px] h-[38px] shrink-0 rounded-[12px] flex items-center justify-center text-[13px] font-bold" style={{ background: tile.bg, color: tile.color }}>
          {inizialiCliente(gruppo.nome)}
        </div>
        <div className="flex-1 min-w-[200px] flex flex-col gap-0.5">
          <span className="text-[15px] font-bold text-[#15161c] truncate">{gruppo.nome}</span>
          <div className="flex gap-1.5 flex-wrap text-[12.5px] text-[#9095a1]">
            <span>
              <span className="scheda-font-mono">{n}</span> {n === 1 ? "contratto" : "contratti"}
              {!gruppo.clienteId && (
                <InfoTooltip
                  title="Cliente non ancora creato"
                  description="Il primo contratto crea il cliente quando viene firmato. Da quel momento qui compaiono Contratto aggiuntivo e Rinnova."
                >
                  <span className="underline decoration-dotted underline-offset-2 cursor-help">&nbsp;· il cliente nasce alla firma</span>
                </InfoTooltip>
              )}
            </span>
            {scadenza && (
              <>
                <span>·</span>
                <span className="font-medium" style={{ color: scadenza.tono === "urgente" ? "#b45309" : "#565d6d" }}>
                  {scadenza.giorni === 0 ? "Prossima scadenza oggi" : `Prossima scadenza tra ${giorniFmt(scadenza.giorni)} gg`}
                </span>
              </>
            )}
          </div>
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {/* Anche ad accordion chiuso: è il motivo per cui il cliente non è
              nella lista Clienti. stopPropagation: il clic porta alla scheda,
              non apre l'accordion. */}
          {gruppo.clienteId && tutti.some((c) => c.cliente_in_attesa_avvio) && (
            <InfoTooltip title="Start non pagato" description={SPIEGAZIONE_START_NON_PAGATO}>
              <Link
                to={`/scheda-cliente/${gruppo.clienteId}`}
                onClick={(e) => e.stopPropagation()}
                data-testid={`gruppo-start-non-pagato-${idGruppo}`}
              >
                <Badge tone="danger" dot>Start non pagato</Badge>
              </Link>
            </InfoTooltip>
          )}
          {riepilogoStati(tutti).map((s) => (
            <Badge key={s.chiave} tone={s.tone} dot>{s.n} {s.testo.toLowerCase()}</Badge>
          ))}
        </div>
        <div className="w-8 h-8 shrink-0 rounded-[10px] flex items-center justify-center">
          <ChevronDown
            className={`w-[18px] h-[18px] text-[#565d6d] transition-transform duration-300 ease-[cubic-bezier(.22,.7,.3,1)] motion-reduce:transition-none ${aperto ? "rotate-180" : ""}`}
            strokeWidth={1.8}
          />
        </div>
      </div>

      {montato && (
        <div ref={corpoRef} className="contratti-acc-corpo" inert={!espanso}>
        <div className="border-t border-[#eeeef4] bg-[#fbfbfd] rounded-b-[14px] flex flex-col">
          {gruppo.contratti.map((c, i) => (
            <div key={c.contratto_id} className="contratti-acc-strato" style={{ "--i": i }} data-uscita={!!inUscita[c.contratto_id]}>
              <div className="contratti-acc-strato-interno">
                <RigaContratto c={c} primo={i === 0} {...rigaProps} />
              </div>
            </div>
          ))}
          {gruppo.clienteId && (
            <div style={{ "--i": gruppo.contratti.length }} className="contratti-acc-strato border-t border-[#eeeef4] pt-2.5 pb-3 pl-4 md:pl-[58px] pr-4">
              <InfoTooltip
                title="Contratto aggiuntivo"
                description="Vende servizi in più a questo cliente, accanto a quelli in essere. Parte con anagrafica e documento dell'ultimo contratto; ogni servizio avrà la sua attivazione nei Pagamenti."
              >
                <button
                  type="button"
                  onClick={() => onAggiuntivo(gruppo.clienteId)}
                  className={GHOST_ACCENT_BTN}
                  data-testid={`aggiuntivo-contratto-${gruppo.clienteId}`}
                >
                  <Plus className="w-3.5 h-3.5" strokeWidth={2} />
                  Contratto aggiuntivo
                </button>
              </InfoTooltip>
            </div>
          )}
        </div>
        </div>
      )}
    </div>
    </div>
  );
}

// `ambito` sceglie il catalogo da cui si vendono i servizi e quali contratti
// elencare: la pagina Contratti eventi è questa stessa con ambito "eventi".
export default function ContrattiPage({ ambito = "standard" }) {
  const configAmbito = AMBITI_CONTRATTO[ambito];
  const navigate = useNavigate();
  const [contratti, setContratti] = useState([]);
  const [ricerca, setRicerca] = useState("");
  const [filtroAutore, setFiltroAutore] = useState("tutti");
  const [loading, setLoading] = useState(true);
  const [eliminaTarget, setEliminaTarget] = useState(null); // contratto selezionato per l'eliminazione
  const [confermaEliminaTesto, setConfermaEliminaTesto] = useState("");
  const [eliminando, setEliminando] = useState(false);
  const [inUscita, setInUscita] = useState({}); // contratto_id → true durante l'animazione di uscita
  const [caricandoPdfId, setCaricandoPdfId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [step, setStep] = useState("anagrafica");
  const [direzione, setDirezione] = useState("avanti");
  const [maxStepReached, setMaxStepReached] = useState(0);
  const [anagrafica, setAnagrafica] = useState(ANAGRAFICA_VUOTA);
  const [legale, setLegale] = useState(LEGALE_VUOTO);
  const [saving, setSaving] = useState(false);

  const [contrattoId, setContrattoId] = useState(null);
  const [documentoFronte, setDocumentoFronte] = useState(null); // { url, file_id, size, filename, contentType }
  const [documentoRetro, setDocumentoRetro] = useState(null);
  const [uploadingFronte, setUploadingFronte] = useState(false);
  const [uploadingRetro, setUploadingRetro] = useState(false);
  const [durataMesi, setDurataMesi] = useState(1);
  // "YYYY-MM-DD" scelta a mano nello step durata; null = decorrenza proposta
  // (oggi, o la scadenza del contratto rinnovato).
  const [dataInizio, setDataInizio] = useState(null);
  // Stato del dropdown "Tipo documento" sollevato qui (non locale al componente):
  // serve a DialogContent.onEscapeKeydown per sapere se deve fermare Radix.
  const [dropdownDocAperto, setDropdownDocAperto] = useState(false);
  const [servizi, setServizi] = useState([]); // catalogo, da routes/catalogo_servizi.py
  // Uno o più servizi, nell'ordine in cui sono stati scelti: è l'ordine delle
  // sezioni nel PDF (vedi PUT /contratti/{id}/servizi in routes/contratti.py).
  const [serviziSelezionati, setServiziSelezionati] = useState([]);
  // Servizio aperto nella pagina di personalizzazione dello step 4, o null.
  const [personalizzaId, setPersonalizzaId] = useState(null);
  useEffect(() => {
    setPersonalizzaId(null);
  }, [step, dialogOpen]);
  // Aprendo o chiudendo la pagina si riparte dall'alto: la card da cui si è
  // entrati può essere in fondo all'elenco.
  const corpoWizardRef = useRef(null);
  useEffect(() => {
    corpoWizardRef.current?.scrollTo({ top: 0 });
  }, [personalizzaId]);
  const [anteprimaPdfUrl, setAnteprimaPdfUrl] = useState(null);
  // Scheda di un contratto completato aperta dalla lista ("Vedi scheda"/
  // "Modifica scheda"): riusa lo stesso dialog/wizard con tutti gli step già
  // sbloccati nello stepper. `modalitaSolaVisualizzazione` è true solo se il
  // contratto è già in_attesa di firma o firmato (il backend rifiuta comunque
  // ogni salvataggio in quel caso, 409) — il footer allora mostra solo
  // "Chiudi". `contrattoOriginaleCompletato` distingue una modifica da una
  // creazione, per adattare testo dei bottoni e cosa succede a fine wizard.
  const [modalitaSolaVisualizzazione, setModalitaSolaVisualizzazione] = useState(false);
  const [contrattoOriginaleCompletato, setContrattoOriginaleCompletato] = useState(false);
  // Contratto aggiuntivo o rinnovo di un cliente esistente: null per il primo
  // contratto. Porta cliente, contratto rinnovato, decorrenza e i servizi che
  // il cliente ha già (non selezionabili), da GET /clients/{id}/contratti/precompila.
  const [successivo, setSuccessivo] = useState(null);

  // Dialog "Invia per la firma": si apre a fine wizard e anche dalla lista
  // (rigenerazione/reinvio del link). `invioFirmaContratto` porta i dati del
  // contratto target, `invioFirmaEsito` il risultato di POST .../firma/link.
  const [invioFirmaContratto, setInvioFirmaContratto] = useState(null);
  const [invioFirmaEmail, setInvioFirmaEmail] = useState("");
  const [invioFirmaInviaEmail, setInvioFirmaInviaEmail] = useState(true);
  const [invioFirmaGiorni, setInvioFirmaGiorni] = useState(14);
  const [invioFirmaEsito, setInvioFirmaEsito] = useState(null);
  const [invioFirmaCaricando, setInvioFirmaCaricando] = useState(false);
  const [annullandoFirmaId, setAnnullandoFirmaId] = useState(null);
  const [scaricandoPdfFirmatoId, setScaricandoPdfFirmatoId] = useState(null);

  const stepIndex = STEPS.findIndex((s) => s.key === step);
  const stepAttuale = STEPS[stepIndex];
  const pivaStato = pivaCheck(anagrafica.cf_piva);
  const cfStato = cfCheck(legale.cf);
  const datiFiscaliOk = pivaStato.valida && cfStato.valida;

  // Filtri della lista: tutto lato client, GET /contratti restituisce già
  // l'elenco completo (max 500). Le opzioni del commerciale derivano dai
  // contratti stessi, così compare solo chi ne ha creato almeno uno.
  const autori = Object.values(
    contratti.reduce((acc, c) => {
      if (c.created_by && !acc[c.created_by]) {
        acc[c.created_by] = { id: c.created_by, nome: c.created_by_name || "Utente rimosso" };
      }
      return acc;
    }, {})
  ).sort((a, b) => a.nome.localeCompare(b.nome, "it"));

  const testoRicerca = ricerca.trim().toLowerCase();
  const contrattiFiltrati = contratti.filter((c) => {
    if (filtroAutore !== "tutti" && c.created_by !== filtroAutore) return false;
    if (!testoRicerca) return true;
    const campi = [
      c.anagrafica?.ragione_sociale,
      c.anagrafica?.cf_piva,
      c.anagrafica?.email,
      c.anagrafica?.referente,
      c.legale_rappresentante?.nome,
      c.legale_rappresentante?.cognome,
      `${c.legale_rappresentante?.nome || ""} ${c.legale_rappresentante?.cognome || ""}`,
    ];
    return campi.some((v) => v && String(v).toLowerCase().includes(testoRicerca));
  });

  // Accordion della lista: solo le scelte esplicite dell'utente, per chiave
  // di gruppo; senza scelta vale il default (vedi richiedeAzione). Cambiando
  // la ricerca si riparte dai default, che con una ricerca attiva aprono tutto.
  const [gruppiAperti, setGruppiAperti] = useState({});
  useEffect(() => {
    setGruppiAperti({});
  }, [testoRicerca]);
  const contrattiPerGruppo = new Map(raggruppaPerCliente(contratti).map((g) => [g.chiave, g.contratti]));

  // `silenzioso`: aggiorna la lista senza passare dallo stato di caricamento,
  // che la sostituirebbe per un istante col placeholder (es. dopo un'eliminazione).
  const caricaContratti = async ({ silenzioso = false } = {}) => {
    if (!silenzioso) setLoading(true);
    try {
      const res = await apiFetch(`/contratti?ambito=${ambito}`);
      if (res.ok) {
        const data = await res.json();
        setContratti(data.items || []);
      } else {
        toast.error("Errore nel caricamento dei contratti");
      }
    } catch (e) {
      toast.error("Errore di rete");
    } finally {
      setLoading(false);
    }
  };

  const caricaServizi = async () => {
    try {
      const res = await apiFetch(`/catalogo-servizi?ambito=${ambito}`);
      if (res.ok) {
        const data = await res.json();
        setServizi(data.items || []);
        return data.items || [];
      }
    } catch (e) {
      // Non bloccante: se il catalogo non carica lo step "servizio" lo segnala da sé.
    }
    return [];
  };

  // Passando da Contratti a Contratti eventi il componente resta montato
  // (stessa pagina, altra rotta): si ricarica al cambio di ambito.
  useEffect(() => {
    caricaContratti();
    caricaServizi();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ambito]);

  // Arrivo dalla scheda cliente (/contratti?contratto=<id>): si apre subito la
  // scheda di quel contratto. Il parametro si toglie dall'URL così che chiudere
  // il dialog o ricaricare la pagina non lo riapra.
  const [searchParams, setSearchParams] = useSearchParams();
  const contrattoDaAprire = searchParams.get("contratto");
  useEffect(() => {
    if (!contrattoDaAprire) return;
    setSearchParams({}, { replace: true });
    apriScheda({ contratto_id: contrattoDaAprire });
    // Solo al cambio del parametro: apriScheda cambia identità a ogni render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contrattoDaAprire]);

  // Arrivo dalla scheda cliente con "Contratto aggiuntivo" o "Rinnova"
  // (/contratti?nuovo=aggiuntivo|rinnovo&cliente=<id>[&rinnova=<contratto_id>]).
  const nuovoDaAprire = searchParams.get("nuovo");
  const clienteDaAprire = searchParams.get("cliente");
  const rinnovaDaAprire = searchParams.get("rinnova");
  useEffect(() => {
    if (!nuovoDaAprire || !clienteDaAprire) return;
    setSearchParams({}, { replace: true });
    apriContrattoSuccessivo({ tipo: nuovoDaAprire, clienteId: clienteDaAprire, rinnovaId: rinnovaDaAprire });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nuovoDaAprire, clienteDaAprire, rinnovaDaAprire]);

  const caricaContestoSuccessivo = async ({ tipo, clienteId, rinnovaId, contrattoId: bozzaId }) => {
    const params = new URLSearchParams({ tipo });
    if (rinnovaId) params.set("rinnova_contratto_id", rinnovaId);
    if (bozzaId) params.set("contratto_id", bozzaId);
    const res = await apiFetch(`/clients/${clienteId}/contratti/precompila?${params}`);
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      toast.error(data.detail || "Impossibile preparare il contratto");
      return null;
    }
    return data;
  };

  // Contratto aggiuntivo o rinnovo: il wizard è lo stesso del primo
  // contratto, ma parte con anagrafica e legale rappresentante dell'ultimo
  // contratto del cliente (il documento d'identità lo riusa il backend alla
  // creazione della bozza) e, per un rinnovo, con i servizi del contratto rinnovato.
  const apriContrattoSuccessivo = async ({ tipo, clienteId, rinnovaId }) => {
    try {
      const dati = await caricaContestoSuccessivo({ tipo, clienteId, rinnovaId });
      if (!dati) return;
      // Rinnovo di un contratto dell'altro ambito (es. da Area commerciale):
      // si apre nella pagina giusta, col catalogo dei suoi servizi.
      if (dati.ambito && dati.ambito !== ambito) {
        const params = new URLSearchParams({ nuovo: tipo, cliente: clienteId });
        if (rinnovaId) params.set("rinnova", rinnovaId);
        navigate(`${AMBITI_CONTRATTO[dati.ambito].percorso}?${params}`);
        return;
      }
      apriNuovoContratto();
      setSuccessivo(dati);
      setAnagrafica({ ...ANAGRAFICA_VUOTA, ...dati.anagrafica, sede_cap_auto: false });
      setLegale({ ...LEGALE_VUOTO, ...dati.legale_rappresentante, residente_cap_auto: false });
      // Arrivando dalla scheda cliente il catalogo può non essere ancora caricato.
      const catalogo = servizi.length ? servizi : await caricaServizi();
      const perId = Object.fromEntries(catalogo.map((s) => [s.servizio_id, s]));
      setServiziSelezionati((dati.servizi_suggeriti || []).map((id) => perId[id]).filter(Boolean));
    } catch (e) {
      toast.error("Errore di rete");
    }
  };

  // Riprendendo o aprendo un contratto successivo si ricarica il suo contesto:
  // senza, lo step servizi non saprebbe quali servizi il cliente ha già.
  const ripristinaSuccessivo = async (doc) => {
    if (!doc.tipo || doc.tipo === "primo" || !doc.cliente_id) {
      setSuccessivo(null);
      return;
    }
    const base = { tipo: doc.tipo, cliente_id: doc.cliente_id, rinnova_contratto_id: doc.rinnova_contratto_id, servizi_non_disponibili: [] };
    try {
      const dati = await caricaContestoSuccessivo({
        tipo: doc.tipo, clienteId: doc.cliente_id, rinnovaId: doc.rinnova_contratto_id, contrattoId: doc.contratto_id,
      });
      setSuccessivo(dati ? { ...dati, decorrenza: doc.data_inizio || dati.decorrenza } : base);
    } catch (e) {
      setSuccessivo(base);
    }
  };

  const apriNuovoContratto = () => {
    setSuccessivo(null);
    setAnagrafica(ANAGRAFICA_VUOTA);
    setLegale(LEGALE_VUOTO);
    setContrattoId(null);
    setDocumentoFronte(null);
    setDocumentoRetro(null);
    setServiziSelezionati([]);
    setDurataMesi(1);
    setDataInizio(null);
    setDropdownDocAperto(false);
    if (anteprimaPdfUrl) URL.revokeObjectURL(anteprimaPdfUrl);
    setAnteprimaPdfUrl(null);
    setMaxStepReached(0);
    setModalitaSolaVisualizzazione(false);
    setContrattoOriginaleCompletato(false);
    setStep("anagrafica");
    setDirezione("avanti");
    setDialogOpen(true);
  };

  // La bozza viene creata/aggiornata a metà wizard (fine step "legale" in poi),
  // quindi va ricaricata anche chiudendo il dialog senza completare il
  // contratto — altrimenti l'elenco resta com'era all'apertura della pagina
  // e la bozza appena salvata sembra sparita finché non si ricarica a mano.
  const chiudiDialogEAggiorna = (open) => {
    setDialogOpen(open);
    if (!open) caricaContratti();
  };

  // Eliminazione: per una bozza basta la conferma del AlertDialog, per un
  // contratto completato bisogna anche aver digitato "ELIMINA" per sbloccare
  // il bottone — vedi il testo digitato in confermaEliminaTesto.
  const chiudiDialogElimina = () => {
    setEliminaTarget(null);
    setConfermaEliminaTesto("");
  };

  // Il PDF va scaricato con apiFetch (porta l'header Authorization dal
  // localStorage) e aperto da un blob URL: un window.open diretto sull'endpoint
  // non invierebbe il Bearer token e risponderebbe 401, dato che l'app non usa
  // sessioni via cookie (vedi utils/api.js).
  const scaricaEApriPdf = async (contrattoId) => {
    try {
      const res = await apiFetch(`/contratti/${contrattoId}/pdf`);
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Errore nella generazione del PDF");
      }
    } catch (e) {
      toast.error("Errore di rete");
    }
  };

  const vediPdf = async (c) => {
    setCaricandoPdfId(c.contratto_id);
    await scaricaEApriPdf(c.contratto_id);
    setCaricandoPdfId(null);
  };

  // Stesso pattern di scaricaEApriPdf: l'endpoint è autenticato, quindi serve
  // apiFetch + blob (window.open diretto risponderebbe 401).
  const scaricaPdfFirmato = async (c) => {
    setScaricandoPdfFirmatoId(c.contratto_id);
    try {
      const res = await apiFetch(`/contratti/${c.contratto_id}/pdf-firmato`);
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Errore nel download del PDF firmato");
      }
    } catch (e) {
      toast.error("Errore di rete");
    } finally {
      setScaricandoPdfFirmatoId(null);
    }
  };

  // Apre il dialog "Invia per la firma" per un contratto — sia a fine wizard
  // (contratto appena creato) sia dalla lista (nuovo invio/reinvio).
  const apriInvioFirma = (c) => {
    setInvioFirmaContratto(c);
    setInvioFirmaEmail(c.anagrafica?.email || c.anagrafica?.pec || "");
    setInvioFirmaInviaEmail(true);
    setInvioFirmaGiorni(14);
    setInvioFirmaEsito(null);
  };

  const chiudiInvioFirma = (open) => {
    if (!open) {
      setInvioFirmaContratto(null);
      setInvioFirmaEsito(null);
      caricaContratti();
    }
  };

  // Composizione Gmail precompilata: l'email parte dalla casella Google di
  // chi è connesso al browser e resta nella sua posta inviata. Oggetto e
  // testo arrivano dal server (backend/email_contratto.py), la stessa fonte
  // dell'email automatica.
  const urlGmailFirma = (esito, destinatario) =>
    `https://mail.google.com/mail/?view=cm&to=${encodeURIComponent(destinatario || "")}` +
    `&su=${encodeURIComponent(esito.gmail_oggetto || "")}&body=${encodeURIComponent(esito.gmail_testo || esito.url)}`;

  // conGmail: genera il link senza invio automatico e apre Gmail. La finestra
  // si apre prima della chiamata di rete, altrimenti il browser la tratta
  // come popup non richiesto e la blocca.
  const generaLinkFirma = async (conGmail = false) => {
    if (!invioFirmaContratto) return;
    const finestraGmail = conGmail ? window.open("about:blank", "_blank") : null;
    const inviaEmail = invioFirmaInviaEmail && !conGmail;
    setInvioFirmaCaricando(true);
    try {
      const res = await apiFetch(`/contratti/${invioFirmaContratto.contratto_id}/firma/link`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          invia_email: inviaEmail,
          email: invioFirmaEmail || null,
          giorni_validita: invioFirmaGiorni,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setInvioFirmaEsito(data);
        if (finestraGmail) {
          finestraGmail.location.href = urlGmailFirma(data, invioFirmaEmail || data.destinatario);
          toast.success("Link generato: completa l'invio da Gmail");
        } else if (data.email_inviata) toast.success(`Email inviata a ${data.inviato_a}`);
        else if (inviaEmail && data.email_errore) toast.error(`Link generato, ma l'invio automatico non è riuscito (${data.email_errore}). Usa "Apri in Gmail".`);
        else toast.success("Link di firma generato");
      } else {
        finestraGmail?.close();
        toast.error(data.detail || "Errore nella generazione del link");
      }
    } catch (e) {
      finestraGmail?.close();
      toast.error("Errore di rete");
    } finally {
      setInvioFirmaCaricando(false);
    }
  };

  const apriGmailFirma = () => {
    if (!invioFirmaEsito) return;
    window.open(urlGmailFirma(invioFirmaEsito, invioFirmaEmail || invioFirmaEsito.destinatario), "_blank");
  };

  const copiaLinkFirma = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copiato");
    } catch (e) {
      // Fallback per contesti senza Clipboard API (es. Safari fuori da HTTPS in locale)
      try {
        const el = document.createElement("textarea");
        el.value = url;
        el.style.position = "fixed";
        el.style.opacity = "0";
        document.body.appendChild(el);
        el.select();
        document.execCommand("copy");
        document.body.removeChild(el);
        toast.success("Link copiato");
      } catch (e2) {
        toast.error("Impossibile copiare il link");
      }
    }
  };

  // Ricopia il link già inviato invece di rigenerarlo: "Reinvia" crea un token
  // nuovo e il link che il cliente ha in mano smetterebbe di funzionare.
  const copiaLinkFirmaAttivo = async (c) => {
    try {
      const res = await apiFetch(`/contratti/${c.contratto_id}/firma/link`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data.detail || "Impossibile recuperare il link");
        return;
      }
      if (data.scaduto) {
        toast.error("Il link è scaduto: usa Reinvia per generarne uno nuovo");
        return;
      }
      await copiaLinkFirma(data.url);
    } catch (e) {
      toast.error("Errore di rete");
    }
  };

  const annullaInvioFirma = async (c) => {
    setAnnullandoFirmaId(c.contratto_id);
    try {
      const res = await apiFetch(`/contratti/${c.contratto_id}/firma/revoca`, { method: "POST" });
      if (res.ok) {
        toast.success("Invio annullato");
        caricaContratti();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Errore nell'annullamento");
      }
    } catch (e) {
      toast.error("Errore di rete");
    } finally {
      setAnnullandoFirmaId(null);
    }
  };

  const confermaElimina = async () => {
    if (!eliminaTarget) return;
    setEliminando(true);
    try {
      const res = await apiFetch(`/contratti/${eliminaTarget.contratto_id}`, { method: "DELETE" });
      if (res.ok) {
        toast.success("Contratto eliminato");
        const id = eliminaTarget.contratto_id;
        chiudiDialogElimina();
        // La riga esce animata e solo dopo sparisce dai dati: togliendola
        // subito, il layout salterebbe senza lasciar vedere cosa è cambiato.
        setInUscita((prev) => ({ ...prev, [id]: true }));
        const ridotto = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
        setTimeout(() => {
          setContratti((prev) => prev.filter((c) => c.contratto_id !== id));
          setInUscita(({ [id]: _, ...resto }) => resto);
          caricaContratti({ silenzioso: true });
        }, ridotto ? 0 : DURATA_USCITA_MS);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Errore nell'eliminazione del contratto");
      }
    } catch (e) {
      toast.error("Errore di rete");
    } finally {
      setEliminando(false);
    }
  };

  // Riapertura di una bozza abbandonata: la bozza esiste già da quando è stato
  // completato lo step "legale" (vedi creaBozzaEContinua), quindi ha sempre
  // anagrafica e legale rappresentante — si riprende da "documento", o da
  // "durata" se anche i documenti erano già stati caricati.
  const apriContrattoEsistente = async (c) => {
    try {
      const res = await apiFetch(`/contratti/${c.contratto_id}`);
      if (!res.ok) {
        toast.error("Errore nel caricamento del contratto");
        return;
      }
      const doc = await res.json();
      if (ambitoDi(doc) !== ambito) {
        navigate(`${AMBITI_CONTRATTO[ambitoDi(doc)].percorso}?contratto=${doc.contratto_id}`);
        return;
      }
      await ripristinaSuccessivo(doc);
      setAnagrafica({ ...ANAGRAFICA_VUOTA, ...doc.anagrafica, sede_cap_auto: false });
      setLegale({ ...LEGALE_VUOTO, ...doc.legale_rappresentante, residente_cap_auto: false });
      setContrattoId(doc.contratto_id);

      const di = doc.documento_identita || {};
      const fronte = di.fronte_file_id
        ? { url: di.fronte_url, file_id: di.fronte_file_id, size: di.fronte_size, filename: "documento-fronte", contentType: di.fronte_content_type }
        : null;
      const retro = di.retro_file_id
        ? { url: di.retro_url, file_id: di.retro_file_id, size: di.retro_size, filename: "documento-retro", contentType: di.retro_content_type }
        : null;
      setDocumentoFronte(fronte);
      setDocumentoRetro(retro);
      setServiziSelezionati((doc.servizi || []).map((x) => ({ ...x, ...statoPersonalizzazione(x) })));
      setDurataMesi(doc.durata_mesi || 1);
      setDataInizio(doc.data_inizio ? dataIso(new Date(doc.data_inizio)) : null);
      setDropdownDocAperto(false);

      // Il documento è facoltativo: si riprende dai servizi, e chi vuole
      // caricarlo torna allo step "documento" dallo stepper.
      const stepDaAprire = !(doc.servizi || []).length ? "servizio" : "durata";
      setMaxStepReached(STEPS.findIndex((s) => s.key === stepDaAprire));
      setModalitaSolaVisualizzazione(false);
      setContrattoOriginaleCompletato(false);
      setDirezione("avanti");
      setStep(stepDaAprire);
      setDialogOpen(true);
    } catch (e) {
      toast.error("Errore di rete");
    }
  };

  // "Vedi scheda" / "Modifica scheda" in lista, per un contratto già
  // completato: stesso dialog, con tutti gli step sbloccati nello stepper.
  // Se il contratto è ancora modificabile (non ancora inviato per la firma,
  // o invio annullato) i dati restano editabili e "Indietro"/"Avanti"
  // salvano come nel resto del wizard; se è in_attesa di firma o già firmato
  // il backend rifiuterebbe comunque ogni scrittura (409, vedi
  // _vieta_modifica_se_in_corso in routes/contratti.py), quindi qui si apre
  // in sola lettura con solo "Chiudi" — evita bottoni che darebbero solo un
  // errore.
  const apriScheda = async (c, stepIniziale = "anagrafica") => {
    try {
      const res = await apiFetch(`/contratti/${c.contratto_id}`);
      if (!res.ok) {
        // 404 capita arrivando dalla scheda cliente: eliminare un contratto non
        // azzera il contratto_id sul cliente che ne è nato.
        toast.error(res.status === 404
          ? "Contratto non trovato: potrebbe essere stato eliminato"
          : "Errore nel caricamento del contratto");
        return;
      }
      const doc = await res.json();
      if (ambitoDi(doc) !== ambito) {
        navigate(`${AMBITI_CONTRATTO[ambitoDi(doc)].percorso}?contratto=${doc.contratto_id}`);
        return;
      }
      await ripristinaSuccessivo(doc);
      setAnagrafica({ ...ANAGRAFICA_VUOTA, ...doc.anagrafica, sede_cap_auto: false });
      setLegale({ ...LEGALE_VUOTO, ...doc.legale_rappresentante, residente_cap_auto: false });
      setContrattoId(doc.contratto_id);

      const di = doc.documento_identita || {};
      setDocumentoFronte(di.fronte_file_id
        ? { url: di.fronte_url, file_id: di.fronte_file_id, size: di.fronte_size, filename: "documento-fronte", contentType: di.fronte_content_type }
        : null);
      setDocumentoRetro(di.retro_file_id
        ? { url: di.retro_url, file_id: di.retro_file_id, size: di.retro_size, filename: "documento-retro", contentType: di.retro_content_type }
        : null);
      setServiziSelezionati((doc.servizi || []).map((x) => ({ ...x, ...statoPersonalizzazione(x) })));
      setDurataMesi(doc.durata_mesi || 1);
      setDataInizio(doc.data_inizio ? dataIso(new Date(doc.data_inizio)) : null);
      setDropdownDocAperto(false);

      // PDF definitivo per lo step "Anteprima": già persistito, niente durata_mesi in query.
      if (anteprimaPdfUrl) URL.revokeObjectURL(anteprimaPdfUrl);
      setAnteprimaPdfUrl(null);
      const pdfRes = await apiFetch(`/contratti/${doc.contratto_id}/pdf`);
      if (pdfRes.ok) {
        const blob = await pdfRes.blob();
        setAnteprimaPdfUrl(URL.createObjectURL(blob));
      }

      const statoFirma = doc.stato_firma || "non_inviato";
      setModalitaSolaVisualizzazione(statoFirma === "in_attesa" || statoFirma === "firmato");
      setContrattoOriginaleCompletato(true);
      setMaxStepReached(STEPS.length - 1);
      setDirezione("avanti");
      setStep(stepIniziale);
      setDialogOpen(true);
    } catch (e) {
      toast.error("Errore di rete");
    }
  };

  const vaiAlloStep = (indice) => {
    if (indice === stepIndex || indice > maxStepReached) return;
    setDirezione(indice >= stepIndex ? "avanti" : "indietro");
    setStep(STEPS[indice].key);
  };

  // ---- CAP → comune/provincia (sede legale e residenza condividono la logica) ----
  const risolviCapSede = (valore) => {
    const cap = valore.replace(/\D/g, "").slice(0, 5);
    const hit = CAPS[cap];
    setAnagrafica((prev) => ({
      ...prev,
      sede_cap: cap,
      ...(hit
        ? { sede_comune: hit.comune, sede_prov: hit.prov, sede_cap_auto: true }
        : cap.length < 5 ? { sede_cap_auto: false } : {}),
    }));
  };
  const risolviCapResidenza = (valore) => {
    const cap = valore.replace(/\D/g, "").slice(0, 5);
    const hit = CAPS[cap];
    setLegale((prev) => ({
      ...prev,
      residente_cap: cap,
      ...(hit
        ? { residente_comune: hit.comune, residente_prov: hit.prov, residente_cap_auto: true }
        : cap.length < 5 ? { residente_cap_auto: false } : {}),
    }));
  };

  const vaiAlPassoLegale = () => {
    if (!anagrafica.ragione_sociale.trim() || !anagrafica.cf_piva.trim() || !anagrafica.sede_via.trim()
        || !anagrafica.sede_comune.trim() || !anagrafica.sede_prov.trim() || !anagrafica.sede_cap.trim()) {
      toast.error("Compila almeno Ragione Sociale, C.F./P.IVA e indirizzo della sede");
      return;
    }
    setMaxStepReached((m) => Math.max(m, 1));
    setDirezione("avanti");
    setStep("legale");
  };

  const tornaAllAnagrafica = () => {
    setDirezione("indietro");
    setStep("anagrafica");
  };

  const compilaDatiProvaCasuali = () => {
    const dati = generaDatiProvaCasuali();
    setAnagrafica(dati.anagrafica);
    setLegale(dati.legale);
    toast.success("Campi compilati con dati di prova");
  };

  const creaBozzaEContinua = async () => {
    if (!legale.nome.trim() || !legale.cognome.trim() || !legale.cf.trim()
        || !legale.residente_via.trim() || !legale.residente_comune.trim()
        || !legale.residente_prov.trim() || !legale.residente_cap.trim()) {
      toast.error("Compila i dati del legale rappresentante / delegato");
      return;
    }
    setSaving(true);
    try {
      // Se contrattoId è già valorizzato stiamo riprendendo una bozza (o
      // tornando indietro a correggerla): aggiorna invece di crearne un'altra.
      const url = contrattoId ? `/contratti/${contrattoId}` : "/contratti";
      const method = contrattoId ? "PUT" : "POST";
      const body = { anagrafica, legale_rappresentante: legale };
      if (!contrattoId) body.ambito = ambito;
      if (!contrattoId && successivo) {
        body.tipo = successivo.tipo;
        body.cliente_id = successivo.cliente_id;
        body.rinnova_contratto_id = successivo.rinnova_contratto_id || null;
      }
      const res = await apiFetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const data = await res.json();
        if (!contrattoId) {
          setContrattoId(data.contratto_id);
          // Contratto successivo: il backend ha già copiato il documento
          // d'identità dell'ultimo contratto del cliente.
          const di = data.documento_identita || {};
          if (di.fronte_file_id) {
            setDocumentoFronte({ url: di.fronte_url, file_id: di.fronte_file_id, size: di.fronte_size, filename: "documento-fronte", contentType: di.fronte_content_type });
          }
          if (di.retro_file_id) {
            setDocumentoRetro({ url: di.retro_url, file_id: di.retro_file_id, size: di.retro_size, filename: "documento-retro", contentType: di.retro_content_type });
          }
        }
        setMaxStepReached((m) => Math.max(m, 2));
        setDirezione("avanti");
        setStep("documento");
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Errore nel salvataggio del contratto");
      }
    } catch (e) {
      toast.error("Errore di rete");
    } finally {
      setSaving(false);
    }
  };

  const tornaAlLegale = () => {
    setDirezione("indietro");
    setStep("legale");
  };

  const caricaDocumento = async (lato, file) => {
    if (!FORMATI_DOCUMENTO_CONSENTITI.includes(file.type)) {
      toast.error("Formato non supportato: usa JPG, PNG, WEBP o PDF");
      return;
    }
    if (file.size > DOCUMENTO_MAX_SIZE_BYTES) {
      toast.error("Il file supera i 10MB consentiti");
      return;
    }
    const setUploading = lato === "fronte" ? setUploadingFronte : setUploadingRetro;
    const setDocumento = lato === "fronte" ? setDocumentoFronte : setDocumentoRetro;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("lato", lato);
      formData.append("file", file);
      const res = await apiFetch(`/contratti/${contrattoId}/documento`, {
        method: "POST",
        body: formData,
      });
      if (res.ok) {
        const data = await res.json();
        setDocumento({ url: data.url, file_id: data.file_id, size: file.size, filename: file.name, contentType: file.type });
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Errore nel caricamento del documento");
      }
    } catch (e) {
      toast.error("Errore di rete");
    } finally {
      setUploading(false);
    }
  };

  // Il documento d'identità è facoltativo: nessun controllo sugli upload.
  // Nello step "legale" tipo e numero del documento sono facoltativi: nel PDF
  // si stampa ciò che c'è (_documento in contratti_pdf.py), e se mancano dopo
  // 15 giorni dalla firma l'amministrazione viene avvisata.
  const vaiAlServizio = () => {
    setMaxStepReached((m) => Math.max(m, 3));
    setDirezione("avanti");
    setStep("servizio");
  };

  const tornaAlDocumento = () => {
    setDirezione("indietro");
    setStep("documento");
  };

  const serviziNonDisponibili = successivo?.servizi_non_disponibili || [];
  // Rinnovo: servizi del contratto che si rinnova, da mostrare in evidenza.
  const serviziInCorso = successivo?.tipo === "rinnovo" ? successivo.servizi_suggeriti || [] : [];
  const toggleServizio = (servizio) => {
    if (modalitaSolaVisualizzazione || serviziNonDisponibili.includes(servizio.servizio_id)) return;
    setServiziSelezionati((prev) => (
      prev.some((s) => s.servizio_id === servizio.servizio_id)
        ? prev.filter((s) => s.servizio_id !== servizio.servizio_id)
        : [...prev, servizio]
    ));
  };

  const aggiornaPersonalizzazione = (servizioId, patch) => {
    setServiziSelezionati((prev) => prev.map((s) => (s.servizio_id === servizioId ? { ...s, ...patch } : s)));
  };
  // Listino del servizio: il catalogo attuale, perché è da lì che il backend
  // riparte a ogni salvataggio. Lo snapshot vale solo se il servizio non c'è più.
  const listinoServizio = (s) => servizi.find((c) => c.servizio_id === s.servizio_id) || { ...s, ...(s.listino || {}) };
  // Stesso nome che il backend salva sul contratto (personalizza_servizio).
  const nomeServizio = (s) => `${listinoServizio(s).nome}${personalizzato(s) ? SUFFISSO_PERSONALIZZATO : ""}`;

  const confermaServizioEContinua = async () => {
    if (serviziSelezionati.length === 0) {
      toast.error("Scegli almeno un servizio dal catalogo");
      return;
    }
    if (serviziSelezionati.some((s) => (s.righeSovrapprezzi || []).some((x) => !(Number(x.valore) > 0)))) {
      toast.error("Indica l'importo di ogni sovrapprezzo, o rimuovilo");
      return;
    }
    if (serviziSelezionati.some((s) => (s.righeSconti || []).some((x) => !(Number(x.valore) > 0)))) {
      toast.error("Indica l'importo di ogni sconto, o rimuovilo");
      return;
    }
    if (serviziSelezionati.some((s) => (s.righeSconti || []).some((x) => x.tipo_valore === "percentuale" && Number(x.valore) > 100))) {
      toast.error("Uno sconto in percentuale non può superare il 100%");
      return;
    }
    const errore = serviziSelezionati.map((s) => calcolaServizio(listinoServizio(s), s, successivo?.tipo === "rinnovo").errori[0]).find(Boolean);
    if (errore) {
      toast.error(errore);
      return;
    }
    setSaving(true);
    try {
      const res = await apiFetch(`/contratti/${contrattoId}/servizi`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          servizio_ids: serviziSelezionati.map((s) => s.servizio_id),
          sovrapprezzi: Object.fromEntries(serviziSelezionati.map((s) => [
            s.servizio_id,
            (s.righeSovrapprezzi || []).map((x) => ({
              sovrapprezzo_id: x.sovrapprezzo_id,
              label: x.label.trim(),
              ricorrenza: x.ricorrenza,
              valore: Number(x.valore),
            })),
          ])),
          addon_rimossi: Object.fromEntries(serviziSelezionati.map((s) => [s.servizio_id, s.addonRimossi || []])),
          sconti: Object.fromEntries(serviziSelezionati.map((s) => [
            s.servizio_id,
            (s.righeSconti || []).map((x) => ({
              sconto_id: x.sconto_id,
              voce: x.voce,
              tipo_valore: x.tipo_valore,
              valore: Number(x.valore),
            })),
          ])),
        }),
      });
      if (res.ok) {
        setMaxStepReached((m) => Math.max(m, 4));
        setDirezione("avanti");
        setStep("durata");
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Errore nel salvataggio dei servizi");
      }
    } catch (e) {
      toast.error("Errore di rete");
    } finally {
      setSaving(false);
    }
  };

  const tornaAlServizio = () => {
    setDirezione("indietro");
    setStep("servizio");
  };

  // Step "durata" -> "anteprima": genera un PDF di anteprima (nulla viene
  // persistito, la bozza resta bozza) così l'utente vede tutti i dati prima
  // di creare davvero il contratto. Il backend calcola durata/date al volo
  // (query param `durata_mesi`) proprio perché non sono ancora salvate.
  const vaiAllAnteprima = async () => {
    setSaving(true);
    try {
      const res = await apiFetch(`/contratti/${contrattoId}/pdf?durata_mesi=${durataMesi}&data_inizio=${dataIso(oggi)}`);
      if (res.ok) {
        const blob = await res.blob();
        if (anteprimaPdfUrl) URL.revokeObjectURL(anteprimaPdfUrl);
        setAnteprimaPdfUrl(URL.createObjectURL(blob));
        setMaxStepReached((m) => Math.max(m, 5));
        setDirezione("avanti");
        setStep("anteprima");
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Errore nella generazione dell'anteprima");
      }
    } catch (e) {
      toast.error("Errore di rete");
    } finally {
      setSaving(false);
    }
  };

  const tornaAllaDurata = () => {
    setDirezione("indietro");
    setStep("durata");
  };

  const completaContratto = async () => {
    setSaving(true);
    try {
      const res = await apiFetch(`/contratti/${contrattoId}/durata`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ durata_mesi: durataMesi, data_inizio: dataIso(oggi) }),
      });
      if (res.ok) {
        setDialogOpen(false);
        caricaContratti();
        if (contrattoOriginaleCompletato) {
          // Stiamo correggendo un contratto già completato (aperto da
          // "Modifica scheda"): qui non ha senso riproporre il dialog di
          // invio firma come a fine creazione, si chiude e basta.
          toast.success("Modifiche salvate");
        } else {
          toast.success("Contratto creato");
          // Passo successivo naturale dopo "Crea contratto": invio al cliente
          // per la firma, non più l'apertura automatica del PDF (resta
          // comunque raggiungibile con "Vedi il PDF" dentro questo dialog).
          apriInvioFirma({ contratto_id: contrattoId, anagrafica, stato: "completato" });
        }
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Errore nel completamento del contratto");
      }
    } catch (e) {
      toast.error("Errore di rete");
    } finally {
      setSaving(false);
    }
  };

  // Pagina di personalizzazione aperta: i pulsanti del footer la chiudono e
  // riportano all'elenco dei servizi, senza cambiare step.
  const servizioInPersonalizzazione = step === "servizio" && personalizzaId
    ? serviziSelezionati.find((s) => s.servizio_id === personalizzaId)
    : null;
  const chiudiPersonalizzazione = () => setPersonalizzaId(null);
  const indietroDisponibile = step !== "anagrafica";
  const onIndietro = servizioInPersonalizzazione ? chiudiPersonalizzazione
    : step === "legale" ? tornaAllAnagrafica
    : step === "documento" ? tornaAlLegale
    : step === "servizio" ? tornaAlDocumento
    : step === "durata" ? tornaAlServizio
    : step === "anteprima" ? tornaAllaDurata
    : tornaAllAnagrafica;
  const onAvanti = servizioInPersonalizzazione ? chiudiPersonalizzazione
    : step === "anagrafica" ? vaiAlPassoLegale
    : step === "legale" ? creaBozzaEContinua
    : step === "documento" ? vaiAlServizio
    : step === "servizio" ? confermaServizioEContinua
    : step === "durata" ? vaiAllAnteprima
    : completaContratto;
  const testoAvanti = servizioInPersonalizzazione ? "Conferma personalizzazione"
    : step === "anteprima" ? (contrattoOriginaleCompletato ? "Salva modifiche" : "Crea contratto")
    : step === "durata" && saving ? "Generazione anteprima..."
    : step === "legale" && saving ? "Creazione..." : "Avanti";
  const testIdAvanti = servizioInPersonalizzazione ? "personalizzazione-conferma-btn"
    : step === "anteprima" ? "crea-contratto-btn" : "passo-avanti-btn";

  // Un rinnovo decorre dalla scadenza del contratto che rinnova (calcolata dal
  // backend), non da oggi. È solo la proposta: nello step durata si può
  // scegliere un'altra data, anche passata, e da lì si ricalcola la scadenza.
  const decorrenzaProposta = successivo?.tipo === "rinnovo" && successivo.decorrenza ? new Date(successivo.decorrenza) : new Date();
  const oggi = dataInizio ? new Date(`${dataInizio}T00:00:00`) : decorrenzaProposta;
  const dataFine = new Date(oggi);
  dataFine.setMonth(dataFine.getMonth() + durataMesi);

  const animazioneStep = direzione === "avanti" ? "step-anim-fwd" : "step-anim-back";

  const rigaProps = {
    azioni: {
      riprendi: apriContrattoEsistente,
      inviaFirma: apriInvioFirma,
      apriScheda,
      vediPdf,
      scaricaPdfFirmato,
      annullaInvio: annullaInvioFirma,
      copiaLinkFirma: copiaLinkFirmaAttivo,
      rinnova: (c) => apriContrattoSuccessivo({ tipo: "rinnovo", clienteId: c.cliente_id, rinnovaId: c.contratto_id }),
      elimina: setEliminaTarget,
    },
    caricandoPdfId,
    scaricandoPdfFirmatoId,
    annullandoFirmaId,
  };

  return (
    <div className="flex flex-col gap-6" data-testid={ambito === "eventi" ? "contratti-eventi-page" : "contratti-page"}>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-semibold flex items-center gap-2">
            <FileSignature className="w-6 h-6" />
            {configAmbito.titolo}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Il primo contratto crea il cliente alla firma; dopo si aggiungono contratti aggiuntivi e rinnovi
          </p>
        </div>
        <Button onClick={apriNuovoContratto} data-testid="nuovo-contratto-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nuovo contratto
        </Button>
      </div>

      <SectionCard
        icon={FileText}
        iconBg={ICON_TILES.viola.bg}
        iconColor={ICON_TILES.viola.color}
        title="Contratti creati"
        count={loading ? null : contratti.length}
      >
        <div className="flex flex-col gap-[14px]">
          {!loading && contratti.length > 0 && (
            <div className="flex flex-wrap items-center gap-2.5 mb-1" data-testid="contratti-filtri">
              <div className="relative flex-1 min-w-[240px]">
                <Search className="w-4 h-4 text-[#9095a1] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" strokeWidth={1.8} />
                <Input
                  value={ricerca}
                  onChange={(e) => setRicerca(e.target.value)}
                  placeholder="Cerca per ragione sociale, P.IVA, referente, legale rappresentante..."
                  className={`${FIELD_CLS} pl-9`}
                  data-testid="contratti-ricerca"
                />
              </div>
              {/* Con un solo autore il filtro non serve: è il caso del
                  commerciale, a cui GET /contratti restituisce solo i suoi. */}
              {autori.length > 1 && (
              <Select value={filtroAutore} onValueChange={setFiltroAutore}>
                <SelectTrigger className={`${FIELD_CLS} w-[220px]`} data-testid="contratti-filtro-autore">
                  <SelectValue placeholder="Commerciale" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="tutti">Tutti i commerciali</SelectItem>
                  {autori.map((a) => (
                    <SelectItem key={a.id} value={a.id}>{a.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              )}
            </div>
          )}
          {loading ? (
            <EmptyState>Caricamento...</EmptyState>
          ) : contratti.length === 0 ? (
            <div data-testid="contratti-vuoto"><EmptyState>Nessun contratto ancora creato.</EmptyState></div>
          ) : contrattiFiltrati.length === 0 ? (
            <div data-testid="contratti-nessun-risultato"><EmptyState>Nessun contratto corrisponde ai filtri.</EmptyState></div>
          ) : (
            <div className="flex flex-col gap-[14px]" data-testid="contratti-lista">
              {raggruppaPerCliente(contrattiFiltrati).map((g) => {
                const tutti = contrattiPerGruppo.get(g.chiave) || g.contratti;
                const aperto = gruppiAperti[g.chiave] ?? (testoRicerca ? true : tutti.some(richiedeAzione));
                return (
                  <GruppoContratti
                    key={g.chiave}
                    gruppo={g}
                    tutti={tutti}
                    aperto={aperto}
                    onToggle={() => setGruppiAperti((prev) => ({ ...prev, [g.chiave]: !aperto }))}
                    onAggiuntivo={(clienteId) => apriContrattoSuccessivo({ tipo: "aggiuntivo", clienteId })}
                    rigaProps={rigaProps}
                    inUscita={inUscita}
                  />
                );
              })}
            </div>
          )}
        </div>
      </SectionCard>

      <Dialog open={dialogOpen} onOpenChange={chiudiDialogEAggiorna}>
        <DialogContent
          className="contratto-font-ui sm:max-w-[1080px] max-h-[90vh] p-0 gap-0 overflow-hidden flex flex-col rounded-[20px] shadow-[0_1px_2px_rgba(20,22,35,.06),0_24px_60px_-18px_rgba(20,22,35,.22)]"
          // La modale si chiude solo dalla X (o da "Chiudi" in sola visualizzazione):
          // un tap fuori o Escape a metà compilazione farebbero perdere il lavoro.
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => {
            e.preventDefault();
            // Escape chiude comunque il dropdown "Tipo documento" se è aperto.
            if (dropdownDocAperto) setDropdownDocAperto(false);
          }}
          data-testid="nuovo-contratto-dialog"
        >
          {/* Testata fissa: header + stepper, non scorre */}
          <div className="pt-6 px-8 pb-0 pr-10 shrink-0 flex flex-col gap-5">
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-[13px] flex items-center justify-center shrink-0" style={{ background: "#eeeefc" }}>
                <stepAttuale.icona className="w-5 h-5" style={{ color: IND }} strokeWidth={1.8} />
              </div>
              <div className="flex-1 min-w-0">
                <DialogTitle className="m-0 text-[21px] font-bold leading-[1.25] tracking-[-0.01em]" style={{ color: INK900 }}>
                  {stepAttuale.titolo}
                </DialogTitle>
                <p className="mt-1 text-[13px] font-medium" style={{ color: INK400 }}>
                  {modalitaSolaVisualizzazione
                    ? `Sola visualizzazione · ${stepAttuale.label}`
                    : `Step ${stepIndex + 1} di ${STEPS.length}${
                        !contrattoId ? "" : contrattoOriginaleCompletato ? " · modifica contratto" : " · bozza salvata"
                      }`}
                </p>
                {successivo && (
                  <p
                    className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[12.5px] font-semibold"
                    style={{ background: "#eeeefc", color: IND }}
                    data-testid="contratto-successivo-contesto"
                  >
                    {successivo.tipo === "rinnovo" ? <RefreshCw className="w-3.5 h-3.5" strokeWidth={2} /> : <Plus className="w-3.5 h-3.5" strokeWidth={2} />}
                    {successivo.regolarizzazione
                      ? `Regolarizzazione${successivo.cliente_nome ? ` · ${successivo.cliente_nome}` : ""} · dati dalla scheda cliente`
                      : successivo.tipo === "rinnovo"
                      ? `Rinnovo${successivo.rinnovato_servizi ? ` di ${successivo.rinnovato_servizi}` : ""}${successivo.cliente_nome ? ` · ${successivo.cliente_nome}` : ""}${successivo.decorrenza ? ` · dal ${formattaDataItaliana(new Date(successivo.decorrenza))}` : ""}`
                      : `Contratto aggiuntivo${successivo.cliente_nome ? ` · ${successivo.cliente_nome}` : ""}`}
                  </p>
                )}
              </div>
              {IS_LOCAL_DEV && (
                <button
                  type="button"
                  onClick={compilaDatiProvaCasuali}
                  className="flex items-center gap-1.5 border rounded-[10px] px-3 py-2 text-[13px] font-medium shrink-0 transition-colors"
                  style={{ borderColor: "#e6e6ee", color: INK600 }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#c9c9f0"; e.currentTarget.style.color = IND; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e6e6ee"; e.currentTarget.style.color = INK600; }}
                  data-testid="compila-dati-random-btn"
                  title="Solo in locale: compila anagrafica e legale rappresentante con dati di prova"
                >
                  <Shuffle className="w-[15px] h-[15px]" strokeWidth={1.8} />
                  Dati random
                </button>
              )}
            </div>

            <StepperContratto stepIndex={stepIndex} maxStepReached={maxStepReached} onVai={vaiAlloStep} />
          </div>

          {/* Corpo scrollabile: colonna form + rail di riepilogo */}
          <div ref={corpoWizardRef} className="flex-1 overflow-y-auto overflow-x-hidden flex min-h-[430px]">
          {step === "anteprima" ? (
            <div className="flex-1 min-w-0 px-8 py-6 flex flex-col gap-3">
              <p className="text-[13.5px] leading-[1.5]" style={{ color: INK600 }}>
                Verifica tutti i dati prima di creare il contratto: se qualcosa non torna, usa "Indietro" per
                correggerlo. Le date qui sotto sono indicative — verranno fissate alla creazione.
              </p>
              {anteprimaPdfUrl ? (
                <iframe
                  src={anteprimaPdfUrl}
                  title="Anteprima contratto"
                  className="flex-1 w-full rounded-[12px] border"
                  style={{ borderColor: "#e4e4ed", minHeight: 480 }}
                  data-testid="anteprima-pdf-iframe"
                />
              ) : (
                <p className="text-sm italic" style={{ color: INK400 }}>Anteprima non disponibile.</p>
              )}
            </div>
          ) : (
            <>
            <div className="flex-1 min-w-0 px-8 py-6">
              {step === "anagrafica" && (
                <fieldset disabled={modalitaSolaVisualizzazione} className="contratto-sola-lettura min-w-0 m-0 p-0 border-0">
                  <div key="step-anagrafica" className={`flex flex-col gap-6 ${animazioneStep}`}>
                    <div className="flex flex-col gap-[13px]">
                      <EtichettaSezione>Dati aziendali</EtichettaSezione>
                      <Campo
                        label="Ragione sociale"
                        value={anagrafica.ragione_sociale}
                        onChange={(v) => setAnagrafica({ ...anagrafica, ragione_sociale: v })}
                        testId="ragione-sociale-input"
                      />
                      <div className="grid grid-cols-2 gap-[13px]">
                        <CampoValidato
                          label="C.F. / P.IVA"
                          value={anagrafica.cf_piva}
                          onChange={(v) => setAnagrafica({ ...anagrafica, cf_piva: v })}
                          stato={pivaStato}
                          testId="cf-piva-input"
                        />
                        <div className="flex flex-col gap-1.5">
                          <EtichettaCampo>Codice univoco</EtichettaCampo>
                          <input
                            type="text"
                            value={anagrafica.codice_univoco}
                            onChange={(e) => setAnagrafica({ ...anagrafica, codice_univoco: e.target.value })}
                            className={`${INPUT_BASE} ${INPUT_NEUTRAL} contratto-font-mono`}
                            data-testid="codice-univoco-input"
                          />
                          <span className="text-xs" style={{ color: INK400 }}>7 caratteri</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col gap-[13px]">
                      <EtichettaSezione>Sede legale</EtichettaSezione>
                      <div className="grid grid-cols-[1fr_120px] gap-[13px]">
                        <Campo
                          label="Via / Piazza e civico"
                          value={anagrafica.sede_via}
                          onChange={(v) => setAnagrafica({ ...anagrafica, sede_via: v })}
                          testId="sede-via-input"
                        />
                        <div className="flex flex-col gap-1.5">
                          <EtichettaCampo>CAP</EtichettaCampo>
                          <input
                            type="text"
                            value={anagrafica.sede_cap}
                            onChange={(e) => risolviCapSede(e.target.value)}
                            inputMode="numeric"
                            maxLength={5}
                            className={`${INPUT_BASE} ${INPUT_NEUTRAL} contratto-font-mono`}
                            data-testid="sede-cap-input"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-[1fr_100px_1fr] gap-[13px]">
                        <CampoAuto
                          label="Comune"
                          value={anagrafica.sede_comune}
                          onChange={(v) => setAnagrafica({ ...anagrafica, sede_comune: v, sede_cap_auto: false })}
                          auto={anagrafica.sede_cap_auto}
                        />
                        <CampoAuto
                          label="Prov."
                          value={anagrafica.sede_prov}
                          onChange={(v) => setAnagrafica({ ...anagrafica, sede_prov: v.toUpperCase(), sede_cap_auto: false })}
                          auto={anagrafica.sede_cap_auto}
                          mono
                          uppercase
                          maxLength={2}
                        />
                        <Campo
                          label="Stato"
                          value={anagrafica.sede_stato}
                          onChange={(v) => setAnagrafica({ ...anagrafica, sede_stato: v })}
                        />
                      </div>
                      <CapHint auto={anagrafica.sede_cap_auto} cap={anagrafica.sede_cap} />
                    </div>

                    <div className="flex flex-col gap-[13px]">
                      <EtichettaSezione>Contatti</EtichettaSezione>
                      <div className="grid grid-cols-2 gap-[13px]">
                        <Campo
                          label="Referente"
                          value={anagrafica.referente}
                          onChange={(v) => setAnagrafica({ ...anagrafica, referente: v })}
                          testId="referente-input"
                        />
                        <Campo
                          label="C.F. / P.IVA referente"
                          value={anagrafica.referente_cf}
                          onChange={(v) => setAnagrafica({ ...anagrafica, referente_cf: v.toUpperCase() })}
                          mono
                          uppercase
                          testId="referente-cf-input"
                        />
                        <Campo
                          label="Recapito telefonico"
                          value={anagrafica.recapito_telefonico}
                          onChange={(v) => setAnagrafica({ ...anagrafica, recapito_telefonico: v })}
                          mono
                          testId="recapito-telefonico-input"
                        />
                        <Campo
                          label="e-mail"
                          value={anagrafica.email}
                          onChange={(v) => setAnagrafica({ ...anagrafica, email: v })}
                          type="email"
                          testId="email-input"
                        />
                        <Campo
                          label="PEC"
                          value={anagrafica.pec}
                          onChange={(v) => setAnagrafica({ ...anagrafica, pec: v })}
                          type="email"
                          testId="pec-input"
                        />
                      </div>
                    </div>
                  </div>
                </fieldset>
              )}

              {step === "legale" && (
                <fieldset disabled={modalitaSolaVisualizzazione} className="contratto-sola-lettura min-w-0 m-0 p-0 border-0">
                  <div key="step-legale" className={`flex flex-col gap-6 ${animazioneStep}`}>
                    <div className="flex flex-col gap-[13px]">
                      <EtichettaSezione>Dati anagrafici</EtichettaSezione>
                      <div className="grid grid-cols-2 gap-[13px]">
                        <Campo
                          label="Nome"
                          value={legale.nome}
                          onChange={(v) => setLegale({ ...legale, nome: v })}
                          testId="legale-nome-input"
                        />
                        <Campo
                          label="Cognome"
                          value={legale.cognome}
                          onChange={(v) => setLegale({ ...legale, cognome: v })}
                          testId="legale-cognome-input"
                        />
                      </div>
                      <div className="max-w-[340px]">
                        <CampoValidato
                          label="Codice fiscale"
                          value={legale.cf}
                          onChange={(v) => setLegale({ ...legale, cf: v.toUpperCase() })}
                          stato={cfStato}
                          maxLength={16}
                          uppercase
                          testId="legale-cf-input"
                        />
                      </div>
                    </div>

                    <div className="flex flex-col gap-[13px]">
                      <EtichettaSezione>Residenza</EtichettaSezione>
                      <div className="grid grid-cols-[1fr_120px] gap-[13px]">
                        <Campo
                          label="Via / Piazza e civico"
                          value={legale.residente_via}
                          onChange={(v) => setLegale({ ...legale, residente_via: v })}
                          testId="legale-via-input"
                        />
                        <div className="flex flex-col gap-1.5">
                          <EtichettaCampo>CAP</EtichettaCampo>
                          <input
                            type="text"
                            value={legale.residente_cap}
                            onChange={(e) => risolviCapResidenza(e.target.value)}
                            inputMode="numeric"
                            maxLength={5}
                            className={`${INPUT_BASE} ${INPUT_NEUTRAL} contratto-font-mono`}
                            data-testid="legale-cap-input"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-[1fr_100px_1fr] gap-[13px]">
                        <CampoAuto
                          label="Comune"
                          value={legale.residente_comune}
                          onChange={(v) => setLegale({ ...legale, residente_comune: v, residente_cap_auto: false })}
                          auto={legale.residente_cap_auto}
                        />
                        <CampoAuto
                          label="Prov."
                          value={legale.residente_prov}
                          onChange={(v) => setLegale({ ...legale, residente_prov: v.toUpperCase(), residente_cap_auto: false })}
                          auto={legale.residente_cap_auto}
                          mono
                          uppercase
                          maxLength={2}
                        />
                        <Campo
                          label="Stato"
                          value={legale.residente_stato}
                          onChange={(v) => setLegale({ ...legale, residente_stato: v })}
                          testId="legale-stato-input"
                        />
                      </div>
                      <CapHint auto={legale.residente_cap_auto} cap={legale.residente_cap} />
                    </div>

                    <div className="flex flex-col gap-[13px]">
                      <EtichettaSezione>Documento di riconoscimento</EtichettaSezione>
                      <div className="grid grid-cols-2 gap-[13px]">
                        <div className="flex flex-col gap-1.5">
                          <EtichettaCampo>Tipo documento (facoltativo)</EtichettaCampo>
                          <DropdownDocumento
                            value={legale.documento_tipo}
                            onChange={(v) => setLegale({ ...legale, documento_tipo: v })}
                            aperto={dropdownDocAperto}
                            onApertoChange={setDropdownDocAperto}
                            testId="documento-tipo-select"
                          />
                        </div>
                        <Campo
                          label="Numero documento (facoltativo)"
                          value={legale.documento_numero}
                          onChange={(v) => setLegale({ ...legale, documento_numero: v })}
                          mono
                          testId="documento-numero-input"
                        />
                      </div>
                      <Campo
                        label="Rilasciato da (facoltativo)"
                        value={legale.documento_rilasciato_da}
                        onChange={(v) => setLegale({ ...legale, documento_rilasciato_da: v })}
                        testId="documento-rilasciato-da-input"
                      />
                      <div className="grid grid-cols-2 gap-[13px]">
                        <div className="flex flex-col gap-1.5">
                          <EtichettaCampo>Data di rilascio (facoltativa)</EtichettaCampo>
                          <DatePicker
                            value={legale.documento_data_rilascio}
                            onChange={(v) => setLegale({ ...legale, documento_data_rilascio: v })}
                            className={`${INPUT_BASE} ${INPUT_NEUTRAL}`}
                            data-testid="documento-data-rilascio-picker"
                          />
                        </div>
                        <div className="flex flex-col gap-1.5">
                          <EtichettaCampo>Scadenza (facoltativa)</EtichettaCampo>
                          <DatePicker
                            value={legale.documento_scadenza}
                            onChange={(v) => setLegale({ ...legale, documento_scadenza: v })}
                            className={`${INPUT_BASE} ${INPUT_NEUTRAL}`}
                            data-testid="documento-scadenza-picker"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </fieldset>
              )}

              {step === "documento" && (
                <div key="step-documento" className={`flex flex-col gap-[18px] ${animazioneStep}`}>
                  <p className="max-w-[560px] text-[14.5px] leading-[1.55]" style={{ color: INK600 }}>
                    Facoltativo: se lo hai, carica una foto leggibile di entrambi i lati del documento indicato nello step precedente
                    (<strong className="font-semibold" style={{ color: INK900 }}>{legale.documento_tipo || "documento"}</strong>).
                    Puoi proseguire anche senza e aggiungerlo in seguito.
                  </p>
                  <div className="grid grid-cols-2 gap-3.5">
                    <DocumentoCard
                      lato="FRONTE"
                      documento={documentoFronte}
                      uploading={uploadingFronte}
                      onSelectFile={(file) => caricaDocumento("fronte", file)}
                      onRimuovi={() => setDocumentoFronte(null)}
                      bloccato={modalitaSolaVisualizzazione}
                      testId="documento-fronte-dropzone"
                    />
                    <DocumentoCard
                      lato="RETRO"
                      documento={documentoRetro}
                      uploading={uploadingRetro}
                      onSelectFile={(file) => caricaDocumento("retro", file)}
                      onRimuovi={() => setDocumentoRetro(null)}
                      bloccato={modalitaSolaVisualizzazione}
                      testId="documento-retro-dropzone"
                    />
                  </div>
                </div>
              )}

              {servizioInPersonalizzazione && (
                <div key="step-personalizzazione" className="step-anim-fwd">
                  <PaginaPersonalizzazione
                    servizio={servizioInPersonalizzazione}
                    base={listinoServizio(servizioInPersonalizzazione)}
                    rinnovo={successivo?.tipo === "rinnovo"}
                    disabled={modalitaSolaVisualizzazione}
                    onChange={(patch) => aggiornaPersonalizzazione(servizioInPersonalizzazione.servizio_id, patch)}
                    onChiudi={chiudiPersonalizzazione}
                  />
                </div>
              )}

              {step === "servizio" && !servizioInPersonalizzazione && (
                <div key="step-servizio" className={`flex flex-col gap-4 ${animazioneStep}`}>
                  <p className="max-w-[560px] text-[14.5px] leading-[1.55]" style={{ color: INK600 }}>
                    {successivo?.regolarizzazione
                      ? "I servizi attivi nella scheda del cliente partono già selezionati: conferma quelli che entrano nel contratto o aggiungine altri. Non richiede una nuova attivazione, già pagata in passato."
                      : successivo?.tipo === "rinnovo"
                      ? "Conferma i servizi da rinnovare, togli quelli che non proseguono o aggiungine altri. Il rinnovo non richiede una nuova attivazione."
                      : successivo?.tipo === "aggiuntivo"
                        ? "Scegli i servizi da aggiungere a quelli già attivi per il cliente. Ogni servizio avrà la sua attivazione nei pagamenti."
                        : `Scegli uno o più servizi venduti al cliente dal ${configAmbito.catalogo} configurato in Impostazioni. Ogni servizio avrà la sua attivazione nei pagamenti.`}
                  </p>
                  {servizi.length === 0 ? (
                    <p className="text-[13.5px] italic" style={{ color: INK400 }}>
                      Nessun servizio configurato nel catalogo. Aggiungine uno da Impostazioni → {configAmbito.catalogo}.
                    </p>
                  ) : (
                    <div className="flex flex-col gap-2.5">
                      {(() => {
                      const renderServizio = (s) => {
                        const posizione = serviziSelezionati.findIndex((x) => x.servizio_id === s.servizio_id);
                        const selezionato = posizione >= 0;
                        const giaAttivo = serviziNonDisponibili.includes(s.servizio_id);
                        const inCorso = serviziInCorso.includes(s.servizio_id);
                        // Selezionato vale la personalizzazione fatta per il
                        // cliente, altrimenti il listino del catalogo.
                        const rinnovo = successivo?.tipo === "rinnovo";
                        const base = selezionato ? listinoServizio(serviziSelezionati[posizione]) : s;
                        const calcolo = calcolaServizio(base, selezionato ? serviziSelezionati[posizione] : {}, rinnovo);
                        const dalSecondoMese = canoniDalSecondoMese(base, calcolo);
                        const conQuotePercentuali = calcolo.addons.some((a) => !importoFisso(a));
                        const voci = [
                          ...(calcolo.conAttivazione ? [{ chiave: VOCE_ATTIVAZIONE, testo: `Attivazione · ${formattaEuro(calcolo.attivazione)} una tantum` }] : []),
                          ...calcolo.addons.map((a) => ({
                            chiave: a.addon_id,
                            testo: `${formattaAddon(a)}${dalSecondoMese && a.ricorrenza === "mensile" ? " dal 2° mese" : ""}`,
                          })),
                        ];
                        const card = (
                          <button
                            key={s.servizio_id}
                            type="button"
                            onClick={() => toggleServizio(s)}
                            aria-pressed={selezionato}
                            // aria-disabled e non disabled: un pulsante disabled non
                            // riceve gli eventi del mouse e il tooltip col motivo non
                            // si aprirebbe. Il clic è già ignorato da toggleServizio.
                            aria-disabled={giaAttivo}
                            className={`w-full text-left rounded-[13px] border p-4 transition-colors ${giaAttivo ? "cursor-not-allowed opacity-55" : modalitaSolaVisualizzazione ? "cursor-default" : ""}`}
                            style={{
                              ...(selezionato
                                ? { borderColor: "#5b5bd6", background: "#f6f6fd" }
                                : { borderColor: "#e4e4ed", background: giaAttivo ? "#fafafc" : "#fff" }),
                              // Servizio del contratto che si rinnova: bordo sinistro
                              // verde, resta riconoscibile anche se deselezionato.
                              ...(inCorso ? { borderLeft: `4px solid ${SUCCESS}` } : {}),
                            }}
                            data-testid={`servizio-opzione-${s.servizio_id}`}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <div className="flex items-center gap-2">
                                  {selezionato && (serviziSelezionati.length > 1 ? (
                                    <span
                                      className="w-[18px] h-[18px] rounded-full text-[11px] font-bold flex items-center justify-center shrink-0 contratto-font-ui"
                                      style={{ background: IND, color: "#fff" }}
                                      title="Ordine nel contratto"
                                    >
                                      {posizione + 1}
                                    </span>
                                  ) : (
                                    <Check className="w-4 h-4 shrink-0" style={{ color: IND }} strokeWidth={2.4} />
                                  ))}
                                  <span className="text-[15px] font-semibold contratto-font-ui" style={{ color: INK900 }}>{s.nome}</span>
                                  {selezionato && personalizzato(serviziSelezionati[posizione]) && (
                                    <span
                                      className="text-[11.5px] font-semibold px-2 py-0.5 rounded-full contratto-font-ui"
                                      style={{ background: "#eeeefc", color: IND }}
                                      data-testid={`servizio-personalizzato-${s.servizio_id}`}
                                    >
                                      Personalizzato
                                    </span>
                                  )}
                                  {giaAttivo && (
                                    <span className="text-[11.5px] font-medium px-2 py-0.5 rounded-full contratto-font-ui" style={{ background: "#f0f0f5", color: INK600 }}>
                                      Già attivo
                                    </span>
                                  )}
                                  {inCorso && (
                                    <span
                                      className="text-[11.5px] font-semibold px-2 py-0.5 rounded-full contratto-font-ui"
                                      style={selezionato ? { background: "#e7f5ec", color: SUCCESS } : { background: "#fdf1f1", color: DANGER }}
                                      data-testid={`servizio-in-corso-${s.servizio_id}`}
                                    >
                                      {selezionato ? "In corso · si rinnova" : "In corso · non verrà rinnovato"}
                                    </span>
                                  )}
                                </div>
                                {s.descrizione && (
                                  <p className="mt-1 text-[13px] leading-[1.4]" style={{ color: INK600 }}>{s.descrizione}</p>
                                )}
                                {voci.length > 0 && (
                                  <div className="flex flex-wrap gap-1.5 mt-2">
                                    {voci.map((v) => (
                                      <span
                                        key={v.chiave}
                                        className="text-[11.5px] font-medium px-2 py-0.5 rounded-full contratto-font-ui"
                                        style={{ background: "#eeeefc", color: IND }}
                                      >
                                        {v.testo}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                              <div className="flex flex-col items-end shrink-0">
                                <span className="text-[15px] font-bold contratto-font-mono" style={{ color: INK900 }} data-testid={`servizio-primo-mese-${s.servizio_id}`}>
                                  {formattaEuro(primoMese(base, calcolo))}
                                </span>
                                <span className="text-[11.5px] contratto-font-ui" style={{ color: INK400 }}>
                                  {rinnovo ? "al mese" : "primo mese"}{conQuotePercentuali ? " + quota %" : ""}
                                </span>
                              </div>
                            </div>
                          </button>
                        );
                        if (selezionato) {
                          return (
                            <div key={s.servizio_id} className="flex flex-col">
                              {card}
                              <PersonalizzazioneServizio
                                servizio={serviziSelezionati[posizione]}
                                base={listinoServizio(serviziSelezionati[posizione])}
                                rinnovo={successivo?.tipo === "rinnovo"}
                                disabled={modalitaSolaVisualizzazione}
                                onChange={(patch) => aggiornaPersonalizzazione(s.servizio_id, patch)}
                                onPersonalizza={() => setPersonalizzaId(s.servizio_id)}
                              />
                            </div>
                          );
                        }
                        return giaAttivo ? (
                          <InfoTooltip
                            key={s.servizio_id}
                            block
                            title="Servizio non selezionabile"
                            description={motivoServizioNonDisponibile(successivo?.servizi_non_disponibili_provenienza?.[s.servizio_id])}
                          >
                            {card}
                          </InfoTooltip>
                        ) : card;
                      };
                      // Rinnovo: prima i servizi del contratto che si rinnova,
                      // poi il resto del catalogo. Negli altri casi un solo elenco.
                      // In sola visualizzazione solo i servizi del contratto, dal
                      // suo snapshot: il resto del catalogo non è una scelta possibile.
                      const elenco = modalitaSolaVisualizzazione ? serviziSelezionati : servizi;
                      if (serviziInCorso.length === 0) return elenco.map(renderServizio);
                      const inCorso = elenco.filter((s) => serviziInCorso.includes(s.servizio_id));
                      const altri = elenco.filter((s) => !serviziInCorso.includes(s.servizio_id));
                      return (
                        <>
                          <EtichettaSezione>Servizi in corso nel contratto da rinnovare</EtichettaSezione>
                          {inCorso.map(renderServizio)}
                          {altri.length > 0 && (
                            <div className="mt-3 flex flex-col gap-2.5">
                              <EtichettaSezione>Altri servizi del catalogo</EtichettaSezione>
                              {altri.map(renderServizio)}
                            </div>
                          )}
                        </>
                      );
                      })()}
                    </div>
                  )}
                </div>
              )}

              {step === "durata" && (
                <fieldset disabled={modalitaSolaVisualizzazione} className="contratto-sola-lettura min-w-0 m-0 p-0 border-0">
                  <div key="step-durata" className={`flex flex-col gap-5 ${animazioneStep}`}>
                    <div className="flex flex-col gap-3">
                      <EtichettaSezione>Durata</EtichettaSezione>
                      <div className="grid grid-cols-4 gap-3">
                        {DURATA_PRESET.map((p) => {
                          const selezionato = durataMesi === p.mesi;
                          return (
                            <button
                              key={p.mesi}
                              type="button"
                              onClick={() => setDurataMesi(p.mesi)}
                              className="h-12 rounded-[11px] text-[14.5px] font-semibold contratto-font-ui transition-colors"
                              style={selezionato
                                ? { border: "1.5px solid #5b5bd6", background: IND, color: "#fff" }
                                : { border: "1.5px solid #e4e4ed", background: "#fff", color: "#3a3f4d" }}
                              data-testid={`durata-preset-${p.mesi}`}
                            >
                              {p.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="flex flex-col gap-[11px] p-[18px_20px] rounded-[13px] border" style={{ borderColor: "#ededf3" }}>
                      <div className="flex items-baseline justify-between">
                        <span className="text-[13.5px] font-medium" style={{ color: INK600 }}>Durata personalizzata</span>
                        <span className="text-[14.5px] font-bold contratto-font-mono" style={{ color: IND }} data-testid="durata-valore">
                          {durataMesi === 1 ? "1 mese" : `${durataMesi} mesi`}
                        </span>
                      </div>
                      <input
                        type="range"
                        min={1}
                        max={24}
                        step={1}
                        value={durataMesi}
                        onChange={(e) => setDurataMesi(+e.target.value)}
                        className="w-full h-1 accent-[#5b5bd6]"
                        data-testid="durata-slider"
                      />
                      <div className="flex justify-between text-[11.5px] font-medium contratto-font-mono" style={{ color: INK400 }}>
                        <span>1 mese</span>
                        <span>24 mesi</span>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <DatePicker
                        value={dataIso(oggi)}
                        onChange={setDataInizio}
                        renderTrigger={({ open, toggle }) => (
                          <button
                            type="button"
                            onClick={toggle}
                            className="w-full p-[14px_16px] rounded-xl border flex flex-col gap-1 text-left transition-colors hover:bg-[#fafafd]"
                            style={{ borderColor: open ? "#5b5bd6" : "#ececf2" }}
                            data-testid="decorrenza-picker"
                          >
                            <span className="w-full flex items-center justify-between gap-2">
                              <span className="text-[10.5px] font-bold uppercase contratto-font-mono" style={{ color: "#8b8fa8" }}>Decorrenza</span>
                              <CalendarDays className="w-4 h-4 shrink-0" style={{ color: "#9095a1" }} strokeWidth={1.8} />
                            </span>
                            <span className="text-[15px] font-semibold" style={{ color: INK900 }}>{formattaDataItaliana(oggi)}</span>
                          </button>
                        )}
                      />
                      <div className="p-[14px_16px] rounded-xl border flex flex-col gap-1" style={{ borderColor: "#d9d9f3", background: "#f6f6fd" }}>
                        <span className="text-[10.5px] font-bold uppercase contratto-font-mono" style={{ color: IND }}>Scadenza indicativa</span>
                        <span className="text-[15px] font-semibold" style={{ color: INK900 }}>{formattaDataItaliana(dataFine)}</span>
                      </div>
                    </div>
                  </div>
                </fieldset>
              )}
            </div>

            {/* Rail di riepilogo — sempre visibile, valori in binding live con i campi */}
            <div className="hidden lg:flex w-[282px] flex-none flex-col gap-4 p-6" style={{ borderLeft: "1px solid #f0f0f5" }}>
              <span className="text-[11.5px] font-bold uppercase tracking-[.11em]" style={{ color: INK600 }}>Riepilogo</span>

              <div className="flex flex-col gap-0.5">
                <span className="text-xs" style={{ color: INK400 }}>Cliente</span>
                <span className="text-[14.5px] font-semibold leading-[1.35]" style={{ color: INK900 }}>
                  {anagrafica.ragione_sociale || "—"}
                </span>
                <span className="text-[12.5px] leading-[1.45]" style={{ color: "#6b7280" }}>
                  {anagrafica.cf_piva || "—"} · {anagrafica.sede_comune || "—"} ({anagrafica.sede_prov || "—"})
                </span>
              </div>
              <div className="h-px" style={{ background: "#f0f0f5" }} />

              <div className="flex flex-col gap-0.5">
                <span className="text-xs" style={{ color: INK400 }}>Firmatario</span>
                <span className="text-[14.5px] font-semibold leading-[1.35]" style={{ color: INK900 }}>
                  {legale.nome || legale.cognome ? `${legale.nome} ${legale.cognome}`.trim() : "—"}
                </span>
                <span className="text-[12.5px] leading-[1.45]" style={{ color: "#6b7280" }}>
                  {legale.documento_tipo || "—"} · {legale.documento_numero || "—"}
                </span>
              </div>
              <div className="h-px" style={{ background: "#f0f0f5" }} />

              <div className="flex flex-col gap-0.5">
                <span className="text-xs" style={{ color: INK400 }}>{serviziSelezionati.length > 1 ? "Servizi" : "Servizio"}</span>
                {serviziSelezionati.length === 0 ? (
                  <span className="text-[14.5px] font-semibold leading-[1.35]" style={{ color: INK900 }}>—</span>
                ) : serviziSelezionati.map((s) => (
                  <span key={s.servizio_id} className="text-[14.5px] font-semibold leading-[1.35]" style={{ color: INK900 }}>
                    {nomeServizio(s)}
                  </span>
                ))}
                <span className="text-[12.5px] leading-[1.45]" style={{ color: "#6b7280" }}>
                  {serviziSelezionati.length > 0 && successivo?.tipo === "rinnovo"
                    ? `nessuna attivazione (${successivo.regolarizzazione ? "regolarizzazione" : "rinnovo"})`
                    : serviziSelezionati.length > 0
                    ? `${formattaEuro(serviziSelezionati.reduce((t, s) => t + calcolaServizio(listinoServizio(s), s).attivazione, 0))} attivazione${serviziSelezionati.length > 1 ? " totale" : ""}`
                    : "—"}
                </span>
              </div>
              <div className="h-px" style={{ background: "#f0f0f5" }} />

              <div className="flex flex-col gap-0.5">
                <span className="text-xs" style={{ color: INK400 }}>Durata</span>
                <span className="text-[14.5px] font-semibold leading-[1.35]" style={{ color: INK900 }}>
                  {durataMesi === 1 ? "1 mese" : `${durataMesi} mesi`}
                </span>
                <span className="text-[12.5px] leading-[1.45]" style={{ color: "#6b7280" }}>
                  fino al {formattaDataItaliana(dataFine)}
                </span>
              </div>

              <span className="flex-1" />

              {contrattoId && (!documentoFronte || !documentoRetro) && (
                <button
                  type="button"
                  onClick={() => vaiAlloStep(STEPS.findIndex((s) => s.key === "documento"))}
                  disabled={step === "documento"}
                  className="flex items-start gap-2 px-3 py-2.5 rounded-[10px] text-left text-[12.5px] font-medium leading-[1.4] disabled:cursor-default"
                  style={{ background: "#fef3c7", color: "#b45309" }}
                  data-testid="riepilogo-documento-mancante"
                >
                  <AlertCircle className="w-[15px] h-[15px] mt-px shrink-0" strokeWidth={2.2} />
                  <span>
                    {PROMEMORIA_DOCUMENTO}
                    {step !== "documento" && <span className="block mt-0.5 font-semibold underline underline-offset-2">Carica ora</span>}
                  </span>
                </button>
              )}

              <div
                className="flex items-center gap-2 px-3 py-2.5 rounded-[10px] text-[12.5px] font-medium leading-[1.4]"
                style={datiFiscaliOk ? { background: "#f4faf6", color: SUCCESS } : { background: "#fdf5f5", color: DANGER }}
                data-testid="riepilogo-stato-fiscale"
              >
                {datiFiscaliOk ? <Check className="w-[15px] h-[15px] shrink-0" strokeWidth={2.2} /> : <AlertCircle className="w-[15px] h-[15px] shrink-0" strokeWidth={2.2} />}
                {datiFiscaliOk ? "Dati fiscali verificati" : "Controlla P.IVA e codice fiscale"}
              </div>
            </div>
            </>
          )}
          </div>

          {/* Footer fisso */}
          <div className="shrink-0 flex items-center gap-3.5 px-8 py-4" style={{ borderTop: "1px solid #f0f0f5" }}>
            <span className="text-[13px] font-medium" style={{ color: INK400 }}>
              {modalitaSolaVisualizzazione ? "" : !contrattoId ? "" : contrattoOriginaleCompletato ? "Contratto esistente" : "Bozza salvata"}
            </span>
            <span className="flex-1" />
            {modalitaSolaVisualizzazione ? (
              <button
                type="button"
                onClick={() => chiudiDialogEAggiorna(false)}
                className="h-11 px-5 rounded-[11px] border-none text-[14.5px] font-semibold text-white flex items-center gap-2 transition-colors"
                style={{ background: IND, boxShadow: "0 6px 16px -8px rgba(91,91,214,.9)" }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "#4c4cc4"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = IND; }}
                data-testid="chiudi-scheda-btn"
              >
                Chiudi
              </button>
            ) : (
              <>
                {indietroDisponibile && (
                  <button
                    type="button"
                    onClick={onIndietro}
                    className="h-11 px-[18px] rounded-[11px] border bg-white text-[14.5px] font-semibold flex items-center gap-2 transition-colors"
                    style={{ borderColor: "#e4e4ed", color: INK600 }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#c9c9d6"; e.currentTarget.style.color = INK900; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e4e4ed"; e.currentTarget.style.color = INK600; }}
                    data-testid="passo-indietro-btn"
                  >
                    <ChevronLeft className="w-[15px] h-[15px]" strokeWidth={2} />
                    Indietro
                  </button>
                )}
                <button
                  type="button"
                  onClick={onAvanti}
                  disabled={saving}
                  className="h-11 px-5 rounded-[11px] border-none text-[14.5px] font-semibold text-white flex items-center gap-2 transition-colors disabled:opacity-70"
                  style={{ background: IND, boxShadow: "0 6px 16px -8px rgba(91,91,214,.9)" }}
                  onMouseEnter={(e) => { if (!saving) e.currentTarget.style.background = "#4c4cc4"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = IND; }}
                  data-testid={testIdAvanti}
                >
                  {saving ? "Attendere..." : testoAvanti}
                  {!saving && <ChevronRight className="w-[15px] h-[15px]" strokeWidth={2} />}
                </button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!eliminaTarget} onOpenChange={(open) => !open && chiudiDialogElimina()}>
        <AlertDialogContent data-testid="elimina-contratto-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {eliminaTarget?.stato === "bozza" ? "Eliminare la bozza?" : "Eliminare il contratto?"}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="flex flex-col gap-3">
                <span>
                  {eliminaTarget?.stato === "bozza"
                    ? <>La bozza di <strong>{eliminaTarget?.anagrafica?.ragione_sociale}</strong> verrà eliminata definitivamente.</>
                    : <>Il contratto di <strong>{eliminaTarget?.anagrafica?.ragione_sociale}</strong> è completato: l'eliminazione è definitiva e non recuperabile.</>}
                </span>
                {eliminaTarget?.stato !== "bozza" && (
                  <label className="flex flex-col gap-1.5">
                    <span className="text-xs">
                      Digita <strong>ELIMINA</strong> per confermare
                    </span>
                    <input
                      type="text"
                      value={confermaEliminaTesto}
                      onChange={(e) => setConfermaEliminaTesto(e.target.value)}
                      className="h-9 px-3 rounded-md border text-sm"
                      data-testid="conferma-elimina-input"
                      autoComplete="off"
                    />
                  </label>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="elimina-contratto-annulla">Annulla</AlertDialogCancel>
            <AlertDialogAction
              className={buttonVariants({ variant: "destructive" })}
              disabled={eliminando || (eliminaTarget?.stato !== "bozza" && confermaEliminaTesto !== "ELIMINA")}
              onClick={(e) => { e.preventDefault(); confermaElimina(); }}
              data-testid="elimina-contratto-conferma"
            >
              {eliminando ? "Eliminazione..." : "Elimina"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!invioFirmaContratto} onOpenChange={chiudiInvioFirma}>
        <DialogContent className="sm:max-w-[480px]" data-testid="invio-firma-dialog">
          <DialogTitle>Invia il contratto per la firma</DialogTitle>
          {!invioFirmaEsito ? (
            <div className="flex flex-col gap-4">
              <p className="text-sm text-muted-foreground">
                {invioFirmaContratto?.anagrafica?.ragione_sociale} riceverà un link pubblico per vedere il
                contratto, firmarlo a schermo (touch o mouse) oppure caricare il PDF firmato a mano.
              </p>
              <label className="flex items-center gap-2 text-sm font-medium">
                <input
                  type="checkbox"
                  checked={invioFirmaInviaEmail}
                  onChange={(e) => setInvioFirmaInviaEmail(e.target.checked)}
                  data-testid="invio-firma-checkbox-email"
                />
                Invia automaticamente via email
              </label>
              {invioFirmaInviaEmail && (
                <p className="text-xs text-muted-foreground -mt-2">
                  L'email automatica parte a nome di chi ha creato il contratto, e le risposte del cliente arrivano a quella persona.
                  Con "Apri in Gmail" la scrivi tu dalla tua casella.
                </p>
              )}
              <label className="flex flex-col gap-1.5">
                  <span className="text-xs font-medium text-muted-foreground">Indirizzo email destinatario</span>
                  <input
                    type="email"
                    value={invioFirmaEmail}
                    onChange={(e) => setInvioFirmaEmail(e.target.value)}
                    className="h-9 px-3 rounded-md border text-sm"
                    placeholder="cliente@esempio.it"
                    data-testid="invio-firma-email-input"
                  />
                </label>
              <label className="flex flex-col gap-1.5">
                <span className="text-xs font-medium text-muted-foreground">Validità del link</span>
                <select
                  value={invioFirmaGiorni}
                  onChange={(e) => setInvioFirmaGiorni(Number(e.target.value))}
                  className="h-9 px-3 rounded-md border text-sm bg-white"
                  data-testid="invio-firma-giorni-select"
                >
                  <option value={7}>7 giorni</option>
                  <option value={14}>14 giorni</option>
                  <option value={30}>30 giorni</option>
                </select>
              </label>
              {invioFirmaContratto?.contratto_id && (
                <button
                  type="button"
                  className="text-sm underline text-left"
                  style={{ color: IND }}
                  onClick={() => scaricaEApriPdf(invioFirmaContratto.contratto_id)}
                  data-testid="invio-firma-vedi-pdf"
                >
                  Vedi il PDF del contratto
                </button>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => chiudiInvioFirma(false)}>Annulla</Button>
                <Button variant="outline" onClick={() => generaLinkFirma(true)} disabled={invioFirmaCaricando} data-testid="invio-firma-gmail-btn">
                  <Mail className="w-4 h-4 mr-2" />
                  Apri in Gmail
                </Button>
                <Button onClick={() => generaLinkFirma(false)} disabled={invioFirmaCaricando} data-testid="invio-firma-genera-btn">
                  {invioFirmaCaricando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  {invioFirmaInviaEmail ? "Genera e invia" : "Genera link"}
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {invioFirmaEsito.email_inviata ? (
                <p className="text-sm">
                  Email inviata a <strong>{invioFirmaEsito.inviato_a}</strong>. Il link resta valido fino al{" "}
                  {new Date(invioFirmaEsito.scade_at).toLocaleDateString("it-IT")}.
                </p>
              ) : (
                <p className="text-sm">
                  Link generato (valido fino al {new Date(invioFirmaEsito.scade_at).toLocaleDateString("it-IT")}).
                  {invioFirmaEsito.email_errore ? " L'invio automatico non è riuscito: mandalo da Gmail o copia il link." : " Mandalo da Gmail o copialo."}
                </p>
              )}
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  readOnly
                  value={invioFirmaEsito.url}
                  className="h-9 px-3 rounded-md border text-sm flex-1 bg-muted"
                  data-testid="invio-firma-url-input"
                  onFocus={(e) => e.target.select()}
                />
                <Button size="sm" variant="outline" onClick={() => copiaLinkFirma(invioFirmaEsito.url)} data-testid="invio-firma-copia-btn">
                  <Copy className="w-3.5 h-3.5 mr-1.5" />
                  Copia
                </Button>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={apriGmailFirma} data-testid="invio-firma-esito-gmail-btn">
                  <Mail className="w-4 h-4 mr-2" />
                  Apri in Gmail
                </Button>
                <Button onClick={() => chiudiInvioFirma(false)} data-testid="invio-firma-chiudi-btn">Chiudi</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
